// src/components/crm/CustomerFormModal.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, functions } from '../../firebase/config';
import {
    collection, doc, addDoc, setDoc, onSnapshot, serverTimestamp,
    query, where, orderBy, updateDoc, deleteField, getDocs
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { 
    X, Plus, Key, Tag, Send, User, FileText, Calendar, DollarSign, 
    History, MessageSquare, ClipboardList, Edit2, Trash2, Save,
    CheckCircle, AlertCircle, Star, CalendarDays, AlertTriangle, StickyNote,
    Receipt, ArrowUp, ArrowDown, ArrowUpDown, Loader2, MapPin, Building2,
    ChevronDown, ChevronUp
} from 'lucide-react';
import { formatPhoneNumber, formatCurrency, creditSourceLabel } from '../../utils/formatters';
import { can, canTechManageCustomers, PERMISSIONS, ROLES, canCreatePortalLink, canSendPortalInvite } from '../../utils/permissions'; // ROLES: officecust-9a4d2c61; portal: s8portal-4e7b2c95
import { countries, statesDetailed } from '../../utils/data';
import { buildAddressString, validateAddressViaCF } from '../../utils/address';
import ConfirmationModal from '../common/ConfirmationModal';
import CustomerStatusBadge from '../common/CustomerStatusBadge';
import AddNoteModal from './AddNoteModal';
import ViewNoteModal from './ViewNoteModal';
import CustomerQuotesView from './CustomerQuotesView';
import InvoiceDetailsView from '../../views/InvoiceDetailsView';
import InvoiceStatusBadge from '../common/InvoiceStatusBadge';
import CampaignDetailsModal from './CampaignDetailsModal';
import PortalInviteModal from './PortalInviteModal';
import PropertyEditorModal from './PropertyEditorModal'; // PROPERTIES v1 — 7f3a9c21
import RecordAccountPaymentModal from '../common/RecordAccountPaymentModal';
import SpreadCreditModal from '../common/SpreadCreditModal'; // creditspread-e9c3a7f2 -- Seam 15
import NoteComposer from './NoteComposer';
import NoteCard from './NoteCard';
import useOrgNotes from '../../hooks/useOrgNotes';
import { resolveDocTerminology, isQuoteArchived } from '../../utils/quoteSchema'; // r1-p1-docterm-cfm-d9a4e1c7; isQuoteArchived: estdel-f9ac8ba8
import { netInvoiceTotal, isCreditNoteLine } from '../../utils/invoiceNetRevenue'; // netrev-6f2a8c53 (R1d-5) - Payment History: cash only, billed net of credit notes
import { isInvoiceOwing } from '../../utils/invoiceOwing'; // x2bweb-99626d17 — the "Outstanding" figures count owing invoices only
import CustomerTaxExemptionSection from './CustomerTaxExemptionSection'; // taxexempt3-8c4f2a1d

const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

// tlfilter-5b8e2d61 — ONE place that decides which Timeline pill an entry belongs to.
// Both the filtered list and the pill counts call this, so they can never disagree.
// Notes used to be "whatever no other pill caught", so every untagged automated email
// (estimate sends, statements, pre-cnote invoice sends) showed up as a note.
// Tag checks come first; contactType labels are only a fallback for notes written
// without a tag (sendQuoteEmail, sendStatementEmail, and invoice sends before c968ae1).
// If a new automated note writer is added without relatedType, add its label here
// or it will land in Notes again.
const TIMELINE_INVOICE_LABELS = ['Invoice Sent', 'Statement Sent', 'Collection Email'];
// Both words, regardless of the org's current default — an org that switched
// terminology has both "Quote Sent" and "Estimate Sent" in its history.
const TIMELINE_ESTIMATE_LABELS = [
    `${resolveDocTerminology('estimate').word} Sent`,
    `${resolveDocTerminology('quote').word} Sent`,
];
const timelineCategory = (note) => {
    if (note.isCampaign) return 'campaigns';
    if (note.isJob) return 'jobs';
    // ROUND 4c VERIFIER: 1761980000-round4c-imported-history-timeline
    if (note.isImportedHistory) return 'importedHistory';
    // cnote-fe-2c9d5abf — relatedType first (survives wording changes); labels cover untagged history
    if (note.relatedType === 'invoice' || TIMELINE_INVOICE_LABELS.includes(note.contactType)) return 'invoices';
    if (note.contactType === 'Follow-Up') return 'followups';
    if (TIMELINE_ESTIMATE_LABELS.includes(note.contactType)) return 'estimates';
    return 'notes';
};

const CustomerFormModal = ({
    closeModal, editingCustomer, user, customers, onDeleteRequest, 
    onComposeRequest, isEmailConnected, currentUserProfile, initialTab = 'history', 
    openQuoteModal, companySettings, onConvertToInvoice, openInvoiceModal, 
    openJobEditorModal, onRequestJobStart, onConvertToJob, onViewJob,
    onCreated, // doced-8f3a61c2 — optional ({ id, ...data }) after a NEW customer is saved
}) => {
    
    // Org's default country for new customers (read from Company Settings).
    // Falls back to 'CA' only if companySettings hasn't loaded yet.
    const defaultCountry = companySettings?.country || 'CA';

    // r1-p1-docterm-cfm-d9a4e1c7 — org-default plural for the aggregate "Quotes/Estimates" tab label
    const { wordPlural: docWordPlural } = resolveDocTerminology(companySettings?.quoteTerminologyDefault);

    const initialFormState = { 
        firstName: '', lastName: '', company: '', address1: '', address2: '', 
        city: '', state: '', zip: '', country: defaultCountry, email: '', phone: '', 
        cell_phone: '', sales_person_id: '', sales_person_name: '', 
        status: 'New', tags: [], nextAction: '',
        // Weekly Planner scheduling preferences — all optional
        preferredDays: [],
        preferredWindow: 'anytime',
        estimatedJobDuration: '',
        preferredCrewId: '',       // lock this customer to a specific crew in weekly planner
        // Sticky note — customer-specific note that auto-appends to jobs and/or invoices
        jobNote: '',
        jobNoteEnabled: false,
        invoiceNoteEnabled: false,
    };

    const [formData, setFormData] = useState(initialFormState);
    const [activeTab, setActiveTab] = useState(() => {
        if (!editingCustomer && typeof window !== 'undefined' && window.innerWidth < 640) return 'details';
        return initialTab;
    });
    const [allUsers, setAllUsers] = useState([]);
    const [duplicate, setDuplicate] = useState(null);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    
    const [lifetimeValue, setLifetimeValue] = useState(0);

    const [viewingNote, setViewingNote] = useState(null);
    const [contactHistory, setContactHistory] = useState([]);
    const [customerInvoices, setCustomerInvoices] = useState([]);
    const [readOnlyQuotes, setReadOnlyQuotes] = useState([]);

    const [liveAdditionalNotes, setLiveAdditionalNotes] = useState({}); 

    const [isMiscFormOpen, setIsMiscFormOpen] = useState(false);
    const [miscForm, setMiscForm] = useState({ key: '', value: '' });
    const [editingKey, setEditingKey] = useState(null); 
    
    const [outstandingBalance, setOutstandingBalance] = useState(0);
    const [overdueBalance, setOverdueBalance] = useState(0);
    const [overdueInvoicesCount, setOverdueInvoicesCount] = useState(0);

    // Seam 7 — this customer's account credits (live) + the per-credit ledger
    // cache. ledgerEntriesByCredit is keyed by creditId and fetched once per
    // modal-open (see handleToggleCredit); it is reset when the customer changes.
    const [customerCredits, setCustomerCredits] = useState([]);
    const [ledgerEntriesByCredit, setLedgerEntriesByCredit] = useState({});
    const [expandedCreditId, setExpandedCreditId] = useState(null);
    const [loadingCreditId, setLoadingCreditId] = useState(null);

    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [customFieldDefs, setCustomFieldDefs] = useState([]);
    const [isScheduleSaving, setIsScheduleSaving] = useState(false);
    const [customerJobs, setCustomerJobs] = useState([]);
    const [customerAppointments, setCustomerAppointments] = useState([]);
    
    const [allTags, setAllTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [isTagInputFocused, setIsTagInputFocused] = useState(false); 
    
    const [routeGroups, setRouteGroups] = useState([]);
    const [campaignHistory, setCampaignHistory] = useState([]);
    const [viewingCampaign, setViewingCampaign] = useState(null);
    // PROPERTIES v1 — b4e6d180
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [properties, setProperties] = useState(() => editingCustomer?.properties || []);
    const [isPropertyEditorOpen, setIsPropertyEditorOpen] = useState(false);
    const [editingProperty, setEditingProperty] = useState(null);
    const [timelineFilter, setTimelineFilter] = useState('all');

    // PROPERTIES v1 — 3b91d6f4 — keep the properties list live from Firestore so
    // edits/deletes survive closing and reopening the customer (and update in real time).
    useEffect(() => {
        if (!editingCustomer?.id || !currentUserProfile?.organizationId) return;
        const propRef = doc(db, `organizations/${currentUserProfile.organizationId}/customers/${editingCustomer.id}`);
        const unsub = onSnapshot(propRef, (snap) => {
            setProperties(snap.exists() ? (snap.data().properties || []) : []);
        });
        return () => unsub();
    }, [editingCustomer?.id, currentUserProfile?.organizationId]);

    // ROUND 3c VERIFIER: 1761744000-round3c-fixDEFGH-state
    // Imported payment ledger (Round 3a's customers/{cid}/importedPayments).
    // Sort state for the Invoices table and the Payment History ledger.
    const [customerImportedPayments, setCustomerImportedPayments] = useState([]);
    // ROUND 4c VERIFIER: 1761980000-round4c-imported-history-timeline
    // Imported deal records from Pipedrive Deals migration. Surface in the
    // Timeline tab as "Imported History" entries — past activity from before
    // Spatchy. Loaded as a parallel onSnapshot subscription, mirroring the
    // pattern used for importedPayments above.
    const [customerImportedDeals, setCustomerImportedDeals] = useState([]);
    const [invoiceSortBy, setInvoiceSortBy] = useState('date');
    const [invoiceSortDir, setInvoiceSortDir] = useState('desc');
    const [paymentSortBy, setPaymentSortBy] = useState('date');
    const [paymentSortDir, setPaymentSortDir] = useState('desc');

    // Notes & Media (Phase 1.2) — realtime subscription to notes attached to this customer.
    // Hook bails out cleanly when attachedKey is null (i.e. unsaved new customer).
    const notesAttachedKey = editingCustomer?.id ? `customer:${editingCustomer.id}` : null;
    const { notes: customerNotes, loading: notesLoading } = useOrgNotes({
        orgId: currentUserProfile?.organizationId,
        attachedKey: notesAttachedKey,
    });

    const highLevelRoles = ['Admin', 'Manager', 'Owner/System Admin', 'Owner', 'System Admin'];

    const isOwnerOrManager = useMemo(() => {
        if (!editingCustomer) return true;
        if (highLevelRoles.includes(currentUserProfile?.role)) return true;
        return editingCustomer.sales_person_id === user?.uid;
    }, [editingCustomer, currentUserProfile, user]);

    // Financial figures on this surface (LTV, balances, payment history, deal
    // amounts) are hidden from anyone without VIEW_FINANCIALS — Field Technicians
    // never hold it, so a flag-on tech sees the customer record but no money.
    const canViewFinancials = can(currentUserProfile, PERMISSIONS.VIEW_FINANCIALS);

    // quotelock-d5c9a2f6 -- deep-link fallback. initialTab comes from the ?tab= URL
    // param (CrmApp /customers/:id), so a tech hitting ?tab=quotes or ?tab=payments
    // would land on a tab whose button AND body are both gated off, leaving a blank
    // content pane rather than a redirect. Fall back to the 'history' default instead.
    useEffect(() => {
        if (!canViewFinancials && (activeTab === 'quotes' || activeTab === 'payments')) {
            setActiveTab('history');
        }
    }, [canViewFinancials, activeTab]);
    // Edit affordances (Save, sales-rep picker): the existing isOwnerOrManager
    // already mirrors the customers UPDATE rule (isHighLevel || record-owner);
    // OR in the tech opt-in so a flag-on tech can fill the form. Do NOT swap in
    // can(MANAGE_CUSTOMERS) — that would enable Save for Office/Consultant on
    // records they don't own, which the rules then reject.
    // officecust-9a4d2c61 — founder decision (Marcus, 2026-10-04): Office Staff may edit ANY customer
    // (firestore.rules customers update: `isOffice() && sales_person_id untouched`). isOwnerOrManager
    // itself is NOT widened — it also gates Record Payment, credit spread and statements, which stay
    // manager / own-rep only.
    const canEditCustomer = isOwnerOrManager
        || canTechManageCustomers(currentUserProfile, companySettings)
        || currentUserProfile?.role === ROLES.OFFICE;
    // Campaign history (Timeline) is manager-only server-side (campaigns rule =
    // isConsultant). MANAGE_CUSTOMERS holders exactly match that role set and
    // exclude techs, so gate the subscription on it to avoid a permission-denied
    // for techs while keeping Office/Consultant/managers working.
    const canReadCampaigns = can(currentUserProfile, PERMISSIONS.MANAGE_CUSTOMERS);
    // Assigning the sales rep is a management action — MANAGE_CUSTOMERS only, NOT
    // canEditCustomer. A flag-on tech can fill every other field but must not set
    // sales_person_id (incl. to themselves), which would make ownsCustomer() true
    // and reopen the ad-hoc invoice/quote/job powers this whole change closes.
    // r1-seam0c-assigngate-8f4b2e07 — AND canEditCustomer so the control matches
    // what the server will actually accept. MANAGE_CUSTOMERS alone left the rep
    // dropdown ENABLED for Office/Consultant on records they don't own — records
    // whose Save button is already hidden (canEditCustomer) and whose write the
    // customers UPDATE rule rejects outright (isOwner() || isHighLevel() ||
    // techCanManageCustomers, and isOwner reads the PRE-write rep). So it was a
    // dead control, and Seam 0c makes those records far more reachable: the quote
    // picker now shows every customer, so consultants land on other reps' profiles
    // routinely. Techs stay blocked exactly as before — no MANAGE_CUSTOMERS means
    // false regardless of the tech opt-in, which is what keeps a tech from
    // stamping themselves and turning ownsCustomer() true for invoices and jobs.
    // officecust-9a4d2c61 — was `&& canEditCustomer`; now that canEditCustomer is true for Office Staff on
    // EVERY customer, the rep picker needs its own line: managers, or the customer's own rep. So Office
    // Staff keep the picker on their own customers and see it locked on everyone else's — exactly what
    // the rule allows (it refuses a sales_person_id change by Office Staff on an unassigned customer).
    const canAssignSalesRep = can(currentUserProfile, PERMISSIONS.MANAGE_CUSTOMERS) && isOwnerOrManager;

    // Record an offline payment straight to the customer's account (Seam 3). Gated
    // on isOwnerOrManager to mirror the server's CREDIT_MANAGE_ROLES gate; the tab
    // itself is already VIEW_FINANCIALS-only.
    const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);
    // Seam 15 -- the credit whose remaining balance is being spread across open
    // invoices (null = modal closed). Same isOwnerOrManager gate as Record Payment.
    const [spreadCredit, setSpreadCredit] = useState(null);

    // Seam 10 -- send a customer their account statement (period, default last 12 months)
    // straight from the Payment History tab. Reaches ANY customer, incl. credit-only /
    // all-paid accounts the Collections report (totalDue>0) never lists. Same server
    // callable + role gate as Collections; button gated on isOwnerOrManager (below).
    const [sendingStatement, setSendingStatement] = useState(false);
    const [stmtStart, setStmtStart] = useState(() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);
        return d.toISOString().split('T')[0];
    });
    const [stmtEnd, setStmtEnd] = useState(() => new Date().toISOString().split('T')[0]);

    const handleSendStatement = async () => {
        if (!editingCustomer?.id) return;
        if (!editingCustomer.email) return alert("This customer has no email address on file.");
        if (!window.confirm(`Email an account statement for ${stmtStart} to ${stmtEnd} to ${editingCustomer.name || 'this customer'}?`)) return;
        setSendingStatement(true);
        try {
            const sendFn = httpsCallable(functions, 'sendStatementEmail');
            await sendFn({ customerId: editingCustomer.id, type: 'standard', startDate: stmtStart, endDate: stmtEnd });
            alert("Statement sent successfully!");
        } catch (error) {
            console.error(error);
            alert("Failed to send statement: " + error.message);
        } finally {
            setSendingStatement(false);
        }
    };

    const handleEditMisc = (key, value) => {
        setEditingKey(key);
        setMiscForm({ key, value });
        setIsMiscFormOpen(true);
    };

    const handleDeleteMisc = async (key) => {
        if (!window.confirm(`Are you sure you want to delete "${key}"?`)) return;
        const custRef = doc(db, `organizations/${currentUserProfile.organizationId}/customers/${editingCustomer.id}`);
        await updateDoc(custRef, {
            [`additionalNotes.${key}`]: deleteField()
        });
    };

    const handleSaveMisc = async () => {
        if (!miscForm.key.trim() || !miscForm.value.trim()) return;

        const custRef = doc(db, `organizations/${currentUserProfile.organizationId}/customers/${editingCustomer.id}`);
        
        if (editingKey && editingKey !== miscForm.key) {
             await updateDoc(custRef, {
                [`additionalNotes.${editingKey}`]: deleteField(),
                [`additionalNotes.${miscForm.key}`]: miscForm.value
            });
        } else {
            await updateDoc(custRef, {
                [`additionalNotes.${miscForm.key}`]: miscForm.value
            });
        }
        
        setIsMiscFormOpen(false);
        setEditingKey(null);
        setMiscForm({ key: '', value: '' });
    };

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        
        const settingsRef = doc(db, `organizations/${currentUserProfile.organizationId}/settings`, 'customerFields');
        const unsubSettings = onSnapshot(settingsRef, (d) => d.exists() && setCustomFieldDefs(d.data().fields || []));
        
        const tagsQuery = query(collection(db, `organizations/${currentUserProfile.organizationId}/tags`), orderBy('label'));
        const unsubTags = onSnapshot(tagsQuery, (s) => setAllTags(s.docs.map(d => ({ id: d.id, ...d.data() }))));

        const routeGroupsQuery = query(collection(db, `organizations/${currentUserProfile.organizationId}/routeGroups`), orderBy('name'));
        const unsubRouteGroups = onSnapshot(routeGroupsQuery, (s) => setRouteGroups(s.docs.map(d => ({ id: d.id, ...d.data() }))));

        const usersQ = query(collection(db, 'users'), where("organizationId", "==", currentUserProfile.organizationId));
        const unsubUsers = onSnapshot(usersQ, (s) => setAllUsers(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.status !== 'removed')));

        return () => { unsubSettings(); unsubTags(); unsubUsers(); unsubRouteGroups(); };
    }, [currentUserProfile]);

    useEffect(() => {
        if (!editingCustomer?.id || !currentUserProfile?.organizationId) return;

        const invoicesRef = collection(db, `organizations/${currentUserProfile.organizationId}/invoices`);
        const q = query(invoicesRef, where("customerId", "==", editingCustomer.id));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const total = snapshot.docs.reduce((sum, doc) => {
                const data = doc.data();
                if (data.status && data.status.toLowerCase() !== 'draft') {
                    return sum + (parseFloat(data.total) || 0);
                }
                return sum;
            }, 0);
            setLifetimeValue(total);
        });

        return () => unsubscribe();
    }, [editingCustomer?.id, currentUserProfile?.organizationId]);

    useEffect(() => {
        if (editingCustomer) {
            setLiveAdditionalNotes(editingCustomer.additionalNotes || {});
        }

        let data = { ...initialFormState, customData: {}, followUpSchedule: 'None' };

        if (editingCustomer) {
            const name = editingCustomer.name || '';
            const nameParts = name.split(' ');
            data = { 
                ...data, ...editingCustomer,
                firstName: editingCustomer.firstName || nameParts[0] || '',
                lastName: editingCustomer.lastName || nameParts.slice(1).join(' ') || '',
                customData: editingCustomer.customData || {},
                followUpSchedule: editingCustomer.followUpSchedule || 'None',
                tags: editingCustomer.tags || [],
                preferredDays: editingCustomer.preferredDays || [],
                preferredWindow: editingCustomer.preferredWindow || 'anytime',
                estimatedJobDuration: editingCustomer.estimatedJobDuration || '',
                preferredCrewId: editingCustomer.preferredCrewId || '',
                jobNote: editingCustomer.jobNote || '',
                jobNoteEnabled: editingCustomer.jobNoteEnabled || false,
                invoiceNoteEnabled: editingCustomer.invoiceNoteEnabled || false,
                // Self-healing migration: legacy records may have country stored as
                // a full name (e.g. "Canada") or be missing it entirely. Normalize
                // to ISO code so the dropdown displays the right selection.
                country: normalizeCountryToCode(editingCustomer.country),
                // Self-healing: legacy records may have state as a full name
                // ("Ontario") from CSV import or earlier form writes. Normalize
                // to 2-letter code so the dropdown displays the right selection.
                state: normalizeStateToCode(editingCustomer.state, editingCustomer.country),
            };
        } else if (user && currentUserProfile) {
            // Auto-assign the creator as sales rep ONLY if they're a sales-capable
            // role (MANAGE_CUSTOMERS). A flag-on Field Technician is not a sales rep —
            // leaving sales_person_id blank keeps ownsCustomer() false for them (no
            // ad-hoc invoice/quote/job powers, no isOwner-based edit) and renders as
            // "Unassigned" so office can assign a real rep later.
            if (can(currentUserProfile, PERMISSIONS.MANAGE_CUSTOMERS)) {
                data.sales_person_id = user.uid;
                data.sales_person_name = currentUserProfile.name || user.email;
            }
        }
        
        setFormData(data);

        if (editingCustomer?.id && currentUserProfile?.organizationId) {
            const orgId = currentUserProfile.organizationId;
            const cid = editingCustomer.id;

            const customerRef = doc(db, `organizations/${orgId}/customers/${cid}`);
            const unsubCustomer = onSnapshot(customerRef, (docSnap) => {
                if (docSnap.exists()) {
                    const dbData = docSnap.data();
                    setLiveAdditionalNotes(dbData.additionalNotes || {});
                }
            });

            const notesQ = query(collection(db, `organizations/${orgId}/customers/${cid}/notes`), orderBy('contactDate', 'desc'));
            const unsubNotes = onSnapshot(notesQ, s => setContactHistory(s.docs.map(d => ({ ...d.data(), id: d.id, contactDate: d.data().contactDate?.toDate() }))));

            const invQ = query(collection(db, `organizations/${orgId}/invoices`), where('customerId', '==', cid));
            const unsubInv = onSnapshot(invQ, s => {
                const invs = s.docs.map(d => ({ ...d.data(), id: d.id }));
                setCustomerInvoices(invs);
                let out = 0, overdue = 0, count = 0;
                const now = new Date(); now.setHours(0,0,0,0);
                invs.forEach(i => {
                    if (isInvoiceOwing(i)) { // x2bweb-99626d17 — X2b: same rule as Pay All (was: not Paid / Written Off, so Drafts and Void counted)
                        const due = i.balanceDue ?? i.total ?? 0;
                        out += due;
                        if (i.status === 'Past Due' || (i.dueDate?.toDate() < now)) { overdue += due; count++; }
                    }
                });
                setOutstandingBalance(out);
                setOverdueBalance(overdue);
                setOverdueInvoicesCount(count);
            });

            let unsubQuotes = () => {};
            if (!isOwnerOrManager) {
                const qQuotes = query(collection(db, `organizations/${orgId}/quotes`), where('customerId', '==', cid), orderBy('createdAt', 'desc'));
                unsubQuotes = onSnapshot(qQuotes, s => setReadOnlyQuotes(s.docs.filter(d => !isQuoteArchived(d.data())).map(d => ({ id: d.id, ...d.data() })))); // estdel-f9ac8ba8
            }

            let localJobs = [];
            let localRecurring = [];
            const updateCombinedJobs = () => {
                const allEvents = [...localJobs, ...localRecurring];
                allEvents.sort((a, b) => (b.startAt?.toDate() || 0) - (a.startAt?.toDate() || 0));
                setCustomerJobs(allEvents.filter(e => e.eventType === 'Job'));
                setCustomerAppointments(allEvents.filter(e => ['Appointment', 'Follow-Up'].includes(e.eventType)));
            };

            const jobQ = query(collection(db, `organizations/${orgId}/jobs`), where('clientId', '==', cid));
            const unsubJobs = onSnapshot(jobQ, s => { localJobs = s.docs.map(d => ({ ...d.data(), id: d.id })); updateCombinedJobs(); }); // s6costing-docid: doc id wins over a stale stored `id` field
            
            const recurQ = query(collection(db, `organizations/${orgId}/recurringJobs`), where('clientId', '==', cid));
            const unsubRecur = onSnapshot(recurQ, s => { localRecurring = s.docs.map(d => ({ ...d.data(), id: d.id })); updateCombinedJobs(); }); // s6costing-docid: doc id wins over a stale stored `id` field

            // Campaigns are readable server-side only by isConsultant (managers/
            // office/consultant) — techs would get permission-denied. Skip the
            // subscription for anyone without campaign read access; the Timeline
            // simply shows no campaign entries (campaignHistory stays []).
            let unsubCamp = () => {};
            if (canReadCampaigns) {
                const campQ = query(collection(db, `organizations/${orgId}/campaigns`), where('recipients', 'array-contains', editingCustomer.email));
                unsubCamp = onSnapshot(campQ, s => setCampaignHistory(s.docs.map(d => ({ id: d.id, ...d.data() }))));
            }

            // ROUND 3c VERIFIER: 1761744000-round3c-fixD-imported-payments-sub
            // Subscribe to imported payment ledger (Round 3a's customers/{cid}/importedPayments
            // subcollection). Drives the new Payment History tab.
            const importedPaymentsQ = collection(db, `organizations/${orgId}/customers/${cid}/importedPayments`);
            const unsubImportedPayments = onSnapshot(importedPaymentsQ, s =>
                setCustomerImportedPayments(s.docs.map(d => ({ id: d.id, ...d.data() })))
            );

            // ROUND 4c VERIFIER: 1761980000-round4c-imported-history-timeline
            // Subscribe to the customer's imported deal history (audit subcollection
            // written by Round 4 deal importer). These surface as "Imported History"
            // entries in the Timeline tab. Read-only — server-side writes only.
            const importedDealsQ = collection(db, `organizations/${orgId}/customers/${cid}/importedDeals`);
            const unsubImportedDeals = onSnapshot(importedDealsQ, s =>
                setCustomerImportedDeals(s.docs.map(d => ({ id: d.id, ...d.data() })))
            );

            // acctcredit-7d1b9e42 -- Seam 7: this customer's account credits (live).
            // Drives the "Account Credit" balance card and the credit-history list
            // on the Payment tab. Single-field equality (customerId ==), no orderBy
            // -> no composite index; mirrors InvoiceDetailsView's read. Errors ->
            // empty (the card simply shows $0).
            const creditsQ = query(collection(db, `organizations/${orgId}/customerCredits`), where('customerId', '==', cid));
            const unsubCredits = onSnapshot(
                creditsQ,
                s => setCustomerCredits(s.docs.map(d => ({ id: d.id, ...d.data() }))),
                () => setCustomerCredits([])
            );

            return () => {
                unsubCustomer();
                unsubNotes();
                unsubInv();
                unsubQuotes();
                unsubJobs();
                unsubRecur();
                unsubCamp();
                unsubImportedPayments();
                unsubImportedDeals();
                unsubCredits();
            };
        }
    }, [editingCustomer, user, currentUserProfile, isOwnerOrManager, canReadCampaigns]);

    // Seam 7 — drop the per-credit ledger cache when the customer changes so a
    // freshly-opened record never shows a prior customer's expanded entries.
    useEffect(() => {
        setLedgerEntriesByCredit({});
        setExpandedCreditId(null);
        setLoadingCreditId(null);
    }, [editingCustomer?.id]);

    // Seam 7 — expand a credit and lazily fetch its ledger entries ONCE per open
    // (cached in ledgerEntriesByCredit). Collapse/reopen re-reads nothing. Plain
    // subcollection fetch, no where/orderBy -> no composite index. The credit
    // doc's availableBalance/status stay live via the customerCredits snapshot;
    // only these drill-down entry lines are cache-bound (corrected on reopen).
    const handleToggleCredit = async (creditId) => {
        if (expandedCreditId === creditId) { setExpandedCreditId(null); return; }
        setExpandedCreditId(creditId);
        if (ledgerEntriesByCredit[creditId]) return; // already cached this open
        const orgId = currentUserProfile?.organizationId;
        if (!orgId) return;
        setLoadingCreditId(creditId);
        try {
            const snap = await getDocs(collection(db, `organizations/${orgId}/customerCredits/${creditId}/creditLedgerEntries`));
            const entries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setLedgerEntriesByCredit(prev => ({ ...prev, [creditId]: entries }));
        } catch (e) {
            console.error('Failed to load credit ledger entries', e);
            setLedgerEntriesByCredit(prev => ({ ...prev, [creditId]: [] }));
        } finally {
            setLoadingCreditId(null);
        }
    };

    // ─────────────────────────────────────────────────────────────────
    // ADDRESS FIELD HELPERS — country/state/postal validation
    // ─────────────────────────────────────────────────────────────────
    // Country/state/zip storage convention:
    //   - country: ISO alpha-2 code, e.g. 'CA', 'US', 'GB'
    //   - state:   2-letter code, e.g. 'ON', 'CA' (CA/US); free-text otherwise
    //   - zip:     CA → "A1A 1A1"  |  US → "12345" or "12345-6789"  |  other → free-text

    // Normalize a stored country value to its ISO code.
    // Handles legacy data where country was stored as full name (e.g. "Canada" → "CA").
    // Falls back to defaultCountry if the input doesn't match any known country.
    const normalizeCountryToCode = (value) => {
        if (!value) return defaultCountry;
        const trimmed = String(value).trim();
        if (!trimmed) return defaultCountry;
        // Already a 2-letter code we recognize?
        const upper = trimmed.toUpperCase();
        const byCode = countries.find(c => c.code === upper);
        if (byCode) return byCode.code;
        // Try matching the full name (case-insensitive)
        const lower = trimmed.toLowerCase();
        const byName = countries.find(c => c.name.toLowerCase() === lower);
        if (byName) return byName.code;
        return defaultCountry;
    };

    // Normalize a stored state value to its 2-letter code.
    // Handles legacy records where state was a full name (e.g. "Ontario" → "ON")
    // or already a code. The country argument scopes the lookup to CA or US
    // state lists. Returns empty string if value is empty. For non-CA/US
    // countries (where state is free-text), passes through unchanged. For
    // unknown values that don't match any state, preserves the original —
    // save validation will flag and require the user to pick a valid option.
    const normalizeStateToCode = (value, country) => {
        if (!value) return '';
        const trimmed = String(value).trim();
        if (!trimmed) return '';
        const countryCode = normalizeCountryToCode(country);
        if (countryCode !== 'CA' && countryCode !== 'US') {
            return trimmed;
        }
        const list = statesDetailed[countryCode] || [];
        const upper = trimmed.toUpperCase();
        const byCode = list.find(s => s.code === upper);
        if (byCode) return byCode.code;
        const lower = trimmed.toLowerCase();
        const byName = list.find(s => s.name.toLowerCase() === lower);
        if (byName) return byName.code;
        return trimmed;
    };

    // Build the country dropdown options with Canada and US floated to the top,
    // followed by a divider, then the rest alphabetical.
    const orderedCountries = useMemo(() => {
        const top = [];
        const rest = [];
        countries.forEach(c => {
            if (c.code === 'CA' || c.code === 'US') top.push(c);
            else rest.push(c);
        });
        // Force CA before US in the top section
        top.sort((a, b) => (a.code === 'CA' ? -1 : 1));
        return { top, rest };
    }, []);

    // Postal/ZIP validators
    const CA_POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
    const US_ZIP_RE = /^\d{5}(-\d{4})?$/;

    // Auto-format on blur — turn "k1a0b1" into "K1A 0B1", " 12345 " into "12345"
    const formatPostalOnBlur = (value, country) => {
        if (!value) return '';
        const cleaned = value.toString().trim().toUpperCase();
        if (country === 'CA') {
            const compact = cleaned.replace(/[\s-]/g, '');
            if (compact.length === 6) return `${compact.slice(0,3)} ${compact.slice(3)}`;
            return cleaned;
        }
        if (country === 'US') {
            const digits = cleaned.replace(/\D/g, '');
            if (digits.length === 9) return `${digits.slice(0,5)}-${digits.slice(5)}`;
            if (digits.length === 5) return digits;
            return cleaned;
        }
        return cleaned;
    };

    // Validate the address fields. Returns an object of error messages keyed by field name.
    // Empty object means no errors. Used on save (option a — strict on every save).
    const validateAddress = (data) => {
        const errors = {};
        const country = data.country;
        // Province/state validation
        if (country === 'CA' || country === 'US') {
            const validList = (statesDetailed[country] || []).map(s => s.code);
            if (data.state && !validList.includes(data.state)) {
                errors.state = country === 'CA'
                    ? 'Please select a valid province or territory.'
                    : 'Please select a valid state.';
            }
        }
        // Postal code validation
        if (data.zip) {
            const zipTrim = data.zip.toString().trim();
            if (country === 'CA' && !CA_POSTAL_RE.test(zipTrim)) {
                errors.zip = 'Postal code must be in format A1A 1A1.';
            } else if (country === 'US' && !US_ZIP_RE.test(zipTrim)) {
                errors.zip = 'ZIP must be 12345 or 12345-6789.';
            }
        }
        return errors;
    };

    const [addressErrors, setAddressErrors] = useState({});

    // Geocode-verification state for the address — separate from addressErrors
    // (which holds format/regex check results).
    //   status: 'idle' | 'checking' | 'ok' | 'ambiguous' | 'not_found' | 'error'
    //   When 'ok':         canonical, coords, placeId are populated.
    //   When 'ambiguous':  alternatives is a list of {canonical, coords, placeId}.
    //   When 'not_found' / 'error': message holds a user-visible string.
    const [addressGeocode, setAddressGeocode] = useState({
        status: 'idle',
        canonical: null,
        coords: null,
        placeId: null,
        alternatives: null,
        message: null,
    });

    // ───────────────────────────────────────────────────────────────────────
    // AddressFieldset — renders the 6 address inputs plus inline geocode
    // verification status. Two render sites in this file (create + edit
    // panes) call this so they stay in sync. Reads from closure — no props.
    // ───────────────────────────────────────────────────────────────────────
    const renderAddressFieldset = () => (
        <div className="space-y-3">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Address</h4>
            <input name="address1" placeholder="Address Line 1" value={formData.address1} onChange={handleChange} onBlur={handleAddressFieldBlur} className="p-2 border rounded text-sm w-full min-w-0" />
            <input name="address2" placeholder="Address Line 2 (optional)" value={formData.address2 || ''} onChange={handleChange} onBlur={handleAddressFieldBlur} className="p-2 border rounded text-sm w-full min-w-0" />
            <input name="city" placeholder="City" value={formData.city} onChange={handleChange} onBlur={handleAddressFieldBlur} className="p-2 border rounded text-sm w-full min-w-0" />
            <select
                name="country"
                value={formData.country || defaultCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="p-2 border rounded text-sm w-full min-w-0 bg-white"
            >
                {orderedCountries.top.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                <option disabled>──────────</option>
                {orderedCountries.rest.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
                {(formData.country === 'CA' || formData.country === 'US') ? (
                    <select
                        name="state"
                        value={formData.state || ''}
                        onChange={handleChange}
                        className={`p-2 border rounded text-sm w-full min-w-0 bg-white ${addressErrors.state ? 'border-red-500' : ''}`}
                    >
                        <option value="">{formData.country === 'CA' ? '-- Province / Territory --' : '-- State --'}</option>
                        {(statesDetailed[formData.country] || []).map(s => (
                            <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        name="state"
                        placeholder="Region/State/Province"
                        value={formData.state || ''}
                        onChange={handleChange}
                        className="p-2 border rounded text-sm w-full min-w-0"
                    />
                )}
                <input
                    name="zip"
                    placeholder={formData.country === 'CA' ? 'A1A 1A1' : (formData.country === 'US' ? '12345' : 'Postal Code')}
                    value={formData.zip}
                    onChange={handleChange}
                    onBlur={handlePostalBlur}
                    maxLength={20}
                    className={`p-2 border rounded text-sm w-full min-w-0 ${addressErrors.zip ? 'border-red-500' : ''}`}
                />
            </div>
            {addressErrors.state && <p className="text-xs text-red-600 mt-1">{addressErrors.state}</p>}
            {addressErrors.zip && <p className="text-xs text-red-600 mt-1">{addressErrors.zip}</p>}

            {/* ── Geocode verification status row ───────────────────────── */}
            {addressGeocode.status === 'checking' && (
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                    <Loader2 size={12} className="animate-spin" />
                    <span>Verifying address…</span>
                </div>
            )}
            {addressGeocode.status === 'ok' && (
                <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2 mt-1">
                    <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                        <div className="font-semibold">Address verified</div>
                        {addressGeocode.canonical && (
                            <div className="text-green-600 mt-0.5 flex items-start gap-1">
                                <MapPin size={11} className="flex-shrink-0 mt-0.5" />
                                <span className="break-words">{addressGeocode.canonical}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {addressGeocode.status === 'ok_with_changes' && addressGeocode.differences && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-1 space-y-2">
                    <div className="flex items-start gap-2 text-xs text-amber-800">
                        <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold">Google adjusted your address</div>
                            <div className="mt-1 space-y-0.5">
                                {addressGeocode.differences.map((d, idx) => (
                                    <div key={idx} className="text-amber-700">
                                        <span className="font-medium">{d.label}:</span>{' '}
                                        <span className="line-through text-amber-500">{d.typed}</span>
                                        {' → '}
                                        <span className="font-semibold">{d.canonical}</span>
                                    </div>
                                ))}
                            </div>
                            {addressGeocode.canonical && (
                                <div className="mt-1.5 text-amber-600 flex items-start gap-1">
                                    <MapPin size={11} className="flex-shrink-0 mt-0.5" />
                                    <span className="break-words">{addressGeocode.canonical}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => acceptCanonicalChanges(addressGeocode.differences)}
                            className="flex-1 px-2 py-1 bg-amber-600 text-white text-xs font-semibold rounded hover:bg-amber-700 transition-colors"
                        >
                            Accept changes
                        </button>
                        <button
                            type="button"
                            onClick={keepTypedAddress}
                            className="flex-1 px-2 py-1 bg-white border border-amber-300 text-amber-700 text-xs font-semibold rounded hover:bg-amber-100 transition-colors"
                        >
                            Use what I typed
                        </button>
                    </div>
                </div>
            )}
            {addressGeocode.status === 'ambiguous' && addressGeocode.alternatives?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-1 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-amber-800 font-semibold">
                        <AlertTriangle size={13} />
                        <span>Did you mean one of these?</span>
                    </div>
                    <div className="space-y-1">
                        {addressGeocode.alternatives.map((alt, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => handlePickAddressAlternative(alt)}
                                className="w-full text-left text-xs px-2 py-1.5 bg-white border border-amber-200 rounded hover:border-amber-400 hover:bg-amber-50 flex items-start gap-2"
                            >
                                <MapPin size={11} className="flex-shrink-0 mt-0.5 text-amber-600" />
                                <span className="break-words flex-1">{alt.canonical}</span>
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] text-amber-700">
                        Or save as-is — we'll flag it as unverified.
                    </p>
                </div>
            )}
            {addressGeocode.status === 'not_found' && (
                <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-1">
                    <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                        <div className="font-semibold">Address not verified</div>
                        <div className="text-red-600 mt-0.5">{addressGeocode.message}</div>
                    </div>
                </div>
            )}
            {addressGeocode.status === 'error' && (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                    <span>{addressGeocode.message}</span>
                </div>
            )}
        </div>
    );

    // Change country: clear state/zip (with confirmation) since CA values
    // won't be valid for US and vice versa.
    const handleCountryChange = (newCountry) => {
        const hasStateOrZip = (formData.state && formData.state.trim()) ||
                              (formData.zip && formData.zip.trim());
        if (hasStateOrZip) {
            const ok = window.confirm(
                'Changing the country will clear the Province/State and Postal/ZIP code fields. Continue?'
            );
            if (!ok) return;
        }
        setFormData(p => ({ ...p, country: newCountry, state: '', zip: '' }));
        setAddressErrors({});
    };

    // Postal blur — auto-format, clear stale format error, and (if format
    // looks valid + city is set) kick off geocoding via the validateAddress CF.
    // The CF call is fire-and-forget from the user's perspective: their save
    // is never blocked on it, but the result populates addressGeocode state
    // for inline display.
    // Debounce timer for address verification. We use a ref so the timer ID
    // survives re-renders and we can always clear the most recent one.
    const verifyTimerRef = useRef(null);

    // Compare what the user typed vs. what Google returned. Returns an array
    // of meaningful differences for display, or [] if everything matches.
    //
    // Categories we flag (likely the user typed something wrong):
    //   - postal code (most common case: user typed wrong postal)
    //   - street number (probably wrong address entirely)
    //   - city (Google interpreted differently)
    //
    // Categories we ignore (normal canonical reformatting):
    //   - capitalization
    //   - St/Street, Ave/Avenue, Rd/Road abbreviations
    //   - country code → name expansion
    const detectCanonicalDifferences = (typed, canonical) => {
        if (!canonical || typeof canonical !== 'string') return [];
        const diffs = [];

        // Normalize for case-insensitive compare; preserve original for display.
        const canonLower = canonical.toLowerCase();

        // ── Postal code check ────────────────────────────────────────────
        // Extract Google's postal from canonical regardless of whether the
        // user typed one. Three cases produce a diff:
        //   1. User typed a postal, Google's is different
        //   2. User left it blank, Google found one (fills the field)
        //   3. (We don't flag the reverse — user has one, Google doesn't —
        //      because that'd usually mean the address verified despite
        //      Google not including a postal in the canonical, which we
        //      shouldn't second-guess.)
        const typedZip = (typed.zip || '').trim();
        const caPostalMatch = canonical.match(/[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d/);
        const usPostalMatch = canonical.match(/\b\d{5}(-\d{4})?\b/);
        const googlePostal = caPostalMatch ? caPostalMatch[0] : (usPostalMatch ? usPostalMatch[0] : null);

        if (googlePostal) {
            if (!typedZip) {
                // Case 2: user typed nothing, Google has one. Offer to fill it.
                diffs.push({
                    field: 'zip',
                    label: 'Postal code',
                    typed: '(empty)',
                    canonical: googlePostal,
                });
            } else {
                // Case 1: user typed something. Compare to Google's.
                const typedZipCompact = typedZip.replace(/\s/g, '').toLowerCase();
                const canonHasZip = canonLower.replace(/\s/g, '').includes(typedZipCompact);
                if (!canonHasZip && googlePostal.toLowerCase() !== typedZip.toLowerCase()) {
                    diffs.push({
                        field: 'zip',
                        label: 'Postal code',
                        typed: typedZip,
                        canonical: googlePostal,
                    });
                }
            }
        }

        // ── Street number check ──────────────────────────────────────────
        // Extract leading number from typed address1; compare to canonical.
        // Three cases produce a diff:
        //   1. Both have numbers, and they differ ("123" vs "456")
        //   2. User has a number, canonical doesn't (Google couldn't verify
        //      the specific number, only the street — e.g. user typed
        //      "8 Dewe Ave" when Dewe Ave has no #8)
        //   3. (We don't flag the reverse — canonical has a number user
        //      didn't type — because that's just Google being helpful.)
        const typedAddr1 = (typed.address1 || '').trim();
        const typedNumMatch = typedAddr1.match(/^(\d+\w?)\s/);
        if (typedNumMatch) {
            const typedNum = typedNumMatch[1].toLowerCase();
            // Anchor to start — we want the leading number on a street like
            // "123 Main St", NOT any digit pair that happens to appear later
            // (e.g. inside a postal code "P7A 7N7" where "7A " would match).
            const canonNumMatch = canonical.match(/^(\d+\w?)\s/);
            const canonNum = canonNumMatch ? canonNumMatch[1].toLowerCase() : null;
            if (canonNum && canonNum !== typedNum) {
                // Case 1: numbers differ
                diffs.push({
                    field: 'address1',
                    label: 'Street number',
                    typed: typedNumMatch[1],
                    canonical: canonNumMatch[1],
                });
            } else if (!canonNum) {
                // Case 2: user typed a number, canonical has none. This
                // means Google couldn't verify the specific number exists
                // on that street. Surface it so the user can remove it.
                diffs.push({
                    field: 'address1',
                    label: 'Street number',
                    typed: typedNumMatch[1],
                    canonical: '(not found — street only)',
                    removeNumber: true,
                });
            }
        }

        // ── City check ───────────────────────────────────────────────────
        const typedCity = (typed.city || '').trim();
        if (typedCity && !canonLower.includes(typedCity.toLowerCase())) {
            // Try to extract Google's city. Two canonical formats to handle:
            //   1. With street:  "123 Main St, City, State Postal, Country"
            //   2. Without street: "City, State Postal, Country"
            // Detect by whether the first segment starts with a number.
            const parts = canonical.split(',').map(p => p.trim());
            const firstHasNumber = /^\d+\w?\s/.test(parts[0] || '');
            const googleCity = firstHasNumber
                ? (parts.length >= 3 ? parts[1] : null)
                : (parts.length >= 2 ? parts[0] : null);
            if (googleCity && googleCity.toLowerCase() !== typedCity.toLowerCase()) {
                diffs.push({
                    field: 'city',
                    label: 'City',
                    typed: typedCity,
                    canonical: googleCity,
                });
            }
        }

        return diffs;
    };

    // Apply Google's canonical values to the form fields. Called when the
    // user clicks "Accept changes" in the diff panel.
    const acceptCanonicalChanges = (diffs) => {
        const updates = {};
        diffs.forEach(d => {
            if (d.field === 'zip')  updates.zip  = d.canonical;
            if (d.field === 'city') updates.city = d.canonical;
            if (d.field === 'address1') {
                const rest = (formData.address1 || '').replace(/^(\d+\w?)\s/, '');
                if (d.removeNumber) {
                    // Canonical has no number — Google couldn't verify the
                    // specific number exists. Strip it from the form.
                    updates.address1 = rest.trim();
                } else {
                    // Replace just the leading number, keep the street name.
                    updates.address1 = `${d.canonical} ${rest}`.trim();
                }
            }
        });
        setFormData(prev => ({ ...prev, ...updates }));
        // Move from 'ok_with_changes' to plain 'ok' since user accepted
        setAddressGeocode(prev => ({ ...prev, status: 'ok', differences: null }));
    };

    // User chose to keep what they typed despite Google's adjustments.
    // Verification stays valid (we have real coords) — we just don't
    // overwrite their fields. The differences are saved on the doc so
    // we can warn later if needed.
    const keepTypedAddress = () => {
        setAddressGeocode(prev => ({ ...prev, status: 'ok', differences: null }));
    };

    // The actual verification call. Pulled out of handlePostalBlur so it
    // can be invoked from the debounced blur handler for any field.
    const runAddressVerification = async (snapshot) => {
        // Bail if no minimum address parts
        if (!snapshot.address1?.trim() || !snapshot.city?.trim()) {
            return;
        }

        setAddressGeocode({
            status: 'checking', canonical: null, coords: null,
            placeId: null, alternatives: null, message: null, differences: null,
        });

        try {
            const result = await validateAddressViaCF(functions, {
                address1: snapshot.address1,
                address2: snapshot.address2,
                city:     snapshot.city,
                state:    snapshot.state,
                zip:      snapshot.zip,
                country:  snapshot.country,
            });
            if (result.status === 'ok') {
                // Partial-FSA case: Google returned only an FSA (e.g. "P7B")
                // instead of a full postal — meaning Google found the area
                // but couldn't confidently resolve the exact location. If
                // the user's FSA matches, trust their more-specific value
                // and continue. If not (or user has none), surface as "not
                // verified" with a clear message naming the FSA. Save still
                // proceeds (without coords) so the user can stick with what
                // they have or fix the postal and re-blur.
                const hasFullPostal = /[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d|\b\d{5}(-\d{4})?\b/.test(result.canonical);
                const fsaOnlyMatch = !hasFullPostal && result.canonical.match(/\b[A-Za-z]\d[A-Za-z]\b/);
                const googleFsa = fsaOnlyMatch ? fsaOnlyMatch[0].toUpperCase() : null;
                if (googleFsa) {
                    const typedFsa = (snapshot.zip || '').replace(/\s/g, '').slice(0, 3).toUpperCase();
                    if (typedFsa !== googleFsa) {
                        setAddressGeocode({
                            status: 'not_found',
                            canonical: null, coords: null, placeId: null,
                            alternatives: null, differences: null,
                            message: `Google found this address but couldn't confirm a complete postal code (only the ${googleFsa} area). Please double-check your postal code, or save as-is.`,
                        });
                        return;
                    }
                    // FSAs match → fall through to normal ok handling below
                }

                // Detect meaningful diffs between typed and Google's canonical
                const diffs = detectCanonicalDifferences(snapshot, result.canonical);
                if (diffs.length > 0) {
                    setAddressGeocode({
                        status: 'ok_with_changes',
                        canonical: result.canonical,
                        coords:    result.coords,
                        placeId:   result.placeId,
                        alternatives: null,
                        differences: diffs,
                        message: null,
                    });
                } else {
                    setAddressGeocode({
                        status: 'ok',
                        canonical: result.canonical,
                        coords:    result.coords,
                        placeId:   result.placeId,
                        alternatives: null,
                        differences: null,
                        message: null,
                    });
                }
            } else if (result.status === 'ambiguous') {
                setAddressGeocode({
                    status: 'ambiguous',
                    canonical: null, coords: null, placeId: null,
                    alternatives: result.alternatives || [],
                    differences: null,
                    message: null,
                });
            } else if (result.status === 'not_found') {
                setAddressGeocode({
                    status: 'not_found',
                    canonical: null, coords: null, placeId: null,
                    alternatives: null, differences: null,
                    message: "We couldn't find this address. You can still save it.",
                });
            } else {
                setAddressGeocode({
                    status: 'error',
                    canonical: null, coords: null, placeId: null,
                    alternatives: null, differences: null,
                    message: 'Verification temporarily unavailable.',
                });
            }
        } catch (err) {
            const code = err?.code || '';
            const message = code === 'functions/resource-exhausted'
                ? 'Address verification rate limit reached. You can still save.'
                : 'Verification temporarily unavailable.';
            setAddressGeocode({
                status: 'error',
                canonical: null, coords: null, placeId: null,
                alternatives: null, differences: null,
                message,
            });
        }
    };

    // Schedule verification with debounce. Cancels any pending verification
    // and reschedules — so a user rapidly tabbing through address fields
    // produces exactly one CF call (after they stop for 300ms).
    const scheduleAddressVerification = (snapshot) => {
        if (verifyTimerRef.current) {
            clearTimeout(verifyTimerRef.current);
        }
        verifyTimerRef.current = setTimeout(() => {
            verifyTimerRef.current = null;
            runAddressVerification(snapshot);
        }, 300);
    };

    // Cleanup the timer on unmount so we don't try to set state on an
    // unmounted component if the modal closes during the debounce window.
    useEffect(() => {
        return () => {
            if (verifyTimerRef.current) {
                clearTimeout(verifyTimerRef.current);
                verifyTimerRef.current = null;
            }
        };
    }, []);

    // Blur handler for the postal field specifically — also runs the format
    // check, then schedules verification with the formatted value.
    const handlePostalBlur = () => {
        const formatted = formatPostalOnBlur(formData.zip, formData.country);
        const updated = { ...formData, zip: formatted };
        setFormData(updated);
        setAddressErrors(p => ({ ...p, zip: undefined }));
        scheduleAddressVerification(updated);
    };

    // Generic blur handler for the other address fields (address1, address2,
    // city). Just schedules verification — no formatting needed.
    const handleAddressFieldBlur = () => {
        scheduleAddressVerification(formData);
    };

    // Called when user picks one of the alternatives from an 'ambiguous' result.
    const handlePickAddressAlternative = (alt) => {
        // When user picks an alternative, run the diff detector against the
        // chosen canonical and apply any meaningful corrections automatically.
        // Picking an alternative is a stronger signal than 'ok_with_changes' —
        // the user explicitly chose this as their address, so we shouldn't
        // make them click "Accept changes" a second time.
        const diffs = detectCanonicalDifferences(formData, alt.canonical);
        if (diffs.length > 0) {
            const updates = {};
            diffs.forEach(d => {
                if (d.field === 'zip')  updates.zip  = d.canonical;
                if (d.field === 'city') updates.city = d.canonical;
                if (d.field === 'address1') {
                    const rest = (formData.address1 || '').replace(/^(\d+\w?)\s/, '');
                    if (d.removeNumber) {
                        // Canonical has no number — strip the bad number
                        // (e.g. user typed "8 Dewe Ave" but #8 doesn't exist
                        // on Dewe Ave). Result: form shows "Dewe Ave".
                        updates.address1 = rest.trim();
                    } else {
                        // Numbers differ — use canonical's number.
                        updates.address1 = `${d.canonical} ${rest}`.trim();
                    }
                }
            });
            setFormData(prev => ({ ...prev, ...updates }));
        }
        setAddressGeocode({
            status: 'ok',
            canonical: alt.canonical,
            coords:    alt.coords,
            placeId:   alt.placeId,
            alternatives: null,
            differences: null,
            message: null,
        });
    };

    // Address-affecting field names. Editing any of these invalidates the
    // current geocode result — user must re-blur the postal field to re-verify.
    const ADDRESS_FIELDS = ['address1', 'address2', 'city', 'state', 'zip', 'country'];

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Invalidate stale geocode result if user edits any address field.
        if (ADDRESS_FIELDS.includes(name) && addressGeocode.status !== 'idle') {
            setAddressGeocode({
                status: 'idle', canonical: null, coords: null,
                placeId: null, alternatives: null, message: null,
            });
        }
        if (name === 'phone' || name === 'cell_phone') {
            // Strip non-digits, cap at 10, then format
            const digits = value.replace(/\D/g, '').slice(0, 10);
            setFormData(p => ({ ...p, [name]: formatPhoneNumber(digits) }));
        } else {
            setFormData(p => ({ ...p, [name]: value }));
        }
    };

    const handleCustomDataChange = (fieldId, value) => setFormData(p => ({...p, customData: { ...p.customData, [fieldId]: value }}));

    const handleTagInputKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag(tagInput);
        }
    };

    // All tag-creation paths store 6-digit hex, but guard against hand-edited
    // docs before concatenating alpha suffixes onto the color.
    const tagChipColor = (color) => (/^#[0-9a-fA-F]{6}$/.test(color || '') ? color : '#4B5563');

    const handleAddTag = async (inputVal) => {
        if (!inputVal || !inputVal.trim()) return;
        const cleanLabel = inputVal.trim();
        const existingTag = allTags.find(t => t.label.toLowerCase() === cleanLabel.toLowerCase());
        
        let tagIdToAdd = null;

        if (existingTag) {
            tagIdToAdd = existingTag.id;
        } else {
            try {
                const newTagRef = await addDoc(collection(db, `organizations/${currentUserProfile.organizationId}/tags`), {
                    label: cleanLabel,
                    color: '#4B5563' 
                });
                tagIdToAdd = newTagRef.id;
            } catch (error) {
                console.error("Error creating tag:", error);
                return;
            }
        }

        if (tagIdToAdd && !formData.tags.includes(tagIdToAdd)) {
            setFormData(prev => ({ ...prev, tags: [...prev.tags, tagIdToAdd] }));
        }
        setTagInput(''); 
    };

    const handleRemoveTag = (tagId) => setFormData(p => ({ ...p, tags: p.tags.filter(t => t !== tagId) }));

    const handleSave = async (e) => {
        if (e) e.preventDefault();

        // Validate address before save (strict — option a in design discussion).
        // Errors set into state so red borders + messages render in the form.
        const addrErrors = validateAddress(formData);
        if (Object.keys(addrErrors).length > 0) {
            setAddressErrors(addrErrors);
            // Surface a one-shot alert so the user knows why the save was blocked
            // even if their cursor is far from the address fields on a long form.
            const messages = Object.values(addrErrors).join('\n');
            alert(`Please fix the following before saving:\n\n${messages}`);
            return;
        }
        setAddressErrors({});

        // NAME-OR-COMPANY GATE b2c3d4e5 - require a person name (first+last) OR a company name.
        // Replaces the old HTML5 required on the name inputs so company-only records can save.
        const hasPerson = (formData.firstName || '').trim() && (formData.lastName || '').trim();
        const hasCompany = (formData.company || '').trim();
        if (!hasPerson && !hasCompany) {
            alert('Enter a first and last name, or a company name.');
            return;
        }

        if (!editingCustomer) {
            const dup = checkForDuplicates();
            if (dup) { setDuplicate(dup); return; }
        }
        await saveCustomerData();
    };

    const checkForDuplicates = () => {
        if (!customers || !Array.isArray(customers)) return null;

        const fieldsToCheck = ['address1', 'phone', 'cell_phone', 'email'];
        for (const c of customers) {
            if (c.id === editingCustomer?.id) continue;
            for (const f of fieldsToCheck) {
                if (formData[f] && c[f] === formData[f]) return { ...c, duplicateField: f };
            }
        }
        return null;
    };

    const saveCustomerData = async () => {
        if (!currentUserProfile?.organizationId) return;
        const path = `organizations/${currentUserProfile.organizationId}/customers`;
        // NAME-OR-COMPANY a1b2c3d4 - keep company as name when there is no person name (mirrors buildCustomerDocBody in functions/index.js)
        const personName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
        const companyName = (formData.company || '').trim();
        const data = { ...formData, name: personName || companyName, lifetimeValue };
        // PROPERTIES v1 — 8c4f1a90 — the properties array is owned exclusively by the Properties
        // tab (its add/edit/delete handlers write it directly). formData carries a stale copy from
        // load, so strip it here — otherwise the main save would re-stamp the old array and undo
        // any property changes made this session.
        delete data.properties;
        // h1cust-7e3b9a52 — the QuickBooks link, import ids and email opt-out are server-owned (rules
        // refuse any change). formData carries a copy from load; if the server re-stamps one while the
        // form is open (an invoice push updates qbDetailsFingerprint), echoing the stale copy would get
        // the whole save refused. Leaving the keys out keeps the stored values (setDoc merge).
        ['quickbooksId', 'qbDetailsFingerprint', 'jobberCustomerId', 'importId', 'importedBy', 'importedAt',
         'finalListImportId', 'finalListImportedAt', 'marketingOptOut', 'marketingOptOutAt', 'marketingOptOutSource']
            .forEach(k => delete data[k]);
        // taxexempt3-8c4f2a1d — the tax exemption is saved ONLY by the server (its own section below,
        // setCustomerTaxExemption). Never echoed: a stale copy must not overwrite or trip the rule.
        delete data.taxExemption;
        // officecust-9a4d2c61 — someone who may NOT change the salesperson (Office Staff on another rep's
        // customer, a flag-on tech) never writes those fields. The picker is locked for them, but the
        // form still carries a copy, and a record with NO sales_person_id field loads as '' — writing
        // that back would count as changing the salesperson and the rule would refuse the whole save.
        // Leaving the keys out keeps the stored values exactly as they are (setDoc merge).
        if (editingCustomer && !canAssignSalesRep) {
            delete data.sales_person_id;
            delete data.sales_person_name;
        }

        // Persist geocode-verification result, if any. We only WRITE these
        // fields when the user actively triggered geocoding in this session
        // (status is 'ok', 'not_found', or 'ambiguous'). If status is 'idle'
        // (legacy record, user didn't touch the address), the existing fields
        // on the doc are preserved unchanged.
        if (addressGeocode.status === 'ok' || addressGeocode.status === 'ok_with_changes') {
            // 'ok_with_changes' means the user saw Google's adjustments and
            // chose to keep their typed values. We still have a real geocode
            // (Google found a real place), so we save the coords + placeId.
            // The canonical is what Google returned, even if the user's
            // address1/city/zip differ — for routing and Maps URLs we trust
            // the placeId Google gave us.
            data.addressVerified   = true;
            data.addressVerifiedAt = serverTimestamp();
            data.addressCanonical  = addressGeocode.canonical;
            data.addressCoords     = addressGeocode.coords;
            data.addressPlaceId    = addressGeocode.placeId;
        } else if (addressGeocode.status === 'not_found') {
            data.addressVerified   = false;
            data.addressVerifiedAt = serverTimestamp();
            data.addressCanonical  = null;
            data.addressCoords     = null;
            data.addressPlaceId    = null;
        } else if (addressGeocode.status === 'ambiguous') {
            // User saw alternatives but didn't pick one; treat as unverified.
            data.addressVerified   = false;
            data.addressVerifiedAt = serverTimestamp();
            data.addressCanonical  = null;
            data.addressCoords     = null;
            data.addressPlaceId    = null;
        }
        // status === 'idle', 'checking', or 'error' → preserve existing fields

        try {
            if (editingCustomer) await setDoc(doc(db, path, editingCustomer.id), { ...data, updatedAt: serverTimestamp() }, { merge: true });
            else {
                const created = await addDoc(collection(db, path), { ...data, createdAt: serverTimestamp() });
                // doced-8f3a61c2 — optional: the estimate page's "+ New customer" puts the new customer
                // straight onto the estimate. No other caller passes onCreated, so nothing else changes.
                if (onCreated) onCreated({ id: created.id, ...data });
            }
            closeModal();
        } catch (e) { console.error(e); }
    };

    const handleStatusUpdateFromNote = async (newStatus) => {
        if (!editingCustomer || !currentUserProfile?.organizationId) return;
        setFormData(prev => ({ ...prev, status: newStatus }));
        const customerDocRef = doc(db, `organizations/${currentUserProfile.organizationId}/customers/${editingCustomer.id}`);
        await updateDoc(customerDocRef, { status: newStatus });
    };

    const handleSaveSchedule = async () => {
        if (!editingCustomer?.id) return;
        setIsScheduleSaving(true);
        try {
            const setSchedule = httpsCallable(functions, 'setFollowUpSchedule');
            await setSchedule({ customerId: editingCustomer.id, scheduleType: formData.followUpSchedule });
            alert('Follow-up schedule updated successfully!');
        } catch (error) { alert(`Failed to update schedule: ${error.message}`); } 
        finally { setIsScheduleSaving(false); }
    };

    const handleSaveNextAction = async () => {
        if (!editingCustomer?.id || !currentUserProfile?.organizationId) return;
        const ref = doc(db, `organizations/${currentUserProfile.organizationId}/customers/${editingCustomer.id}`);
        await updateDoc(ref, { nextAction: formData.nextAction || '' });
    };

    // PROPERTIES v1 — 2c8f5a3e — add/update a service location on the customer doc.
    const handleSaveProperty = async (property) => {
        if (!editingCustomer?.id || !currentUserProfile?.organizationId) return;
        const exists = properties.some(p => p.id === property.id);
        const nextProperties = exists
            ? properties.map(p => (p.id === property.id ? property : p))
            : [...properties, property];
        const ref = doc(db, `organizations/${currentUserProfile.organizationId}/customers/${editingCustomer.id}`);
        try {
            await updateDoc(ref, { properties: nextProperties, updatedAt: serverTimestamp() });
            setProperties(nextProperties);
            setIsPropertyEditorOpen(false);
            setEditingProperty(null);
        } catch (err) {
            console.error('Failed to save property:', err);
            alert('Failed to save the property. Please try again.');
        }
    };

    // PROPERTIES v1 — 2c8f5a3e — remove a service location. Existing jobs keep their snapshot.
    const handleDeleteProperty = async (propertyId) => {
        if (!editingCustomer?.id || !currentUserProfile?.organizationId) return;
        if (!window.confirm('Delete this property? Existing jobs booked at this location keep their saved address and are unaffected.')) return;
        const nextProperties = properties.filter(p => p.id !== propertyId);
        const ref = doc(db, `organizations/${currentUserProfile.organizationId}/customers/${editingCustomer.id}`);
        try {
            await updateDoc(ref, { properties: nextProperties, updatedAt: serverTimestamp() });
            setProperties(nextProperties);
        } catch (err) {
            console.error('Failed to delete property:', err);
            alert('Failed to delete the property. Please try again.');
        }
    };

    const [isRequestingReview, setIsRequestingReview] = useState(false);

    const reviewCooldownDays = useMemo(() => {
        if (!editingCustomer?.lastReviewRequestedAt) return null;
        const last = editingCustomer.lastReviewRequestedAt?.toDate
            ? editingCustomer.lastReviewRequestedAt.toDate()
            : new Date(editingCustomer.lastReviewRequestedAt);
        const daysSince = Math.floor((Date.now() - last.getTime()) / (1000 * 60 * 60 * 24));
        return daysSince < 90 ? { daysSince, daysLeft: 90 - daysSince, sentDate: last } : null;
    }, [editingCustomer]);

    const handleRequestReview = async () => {
        if (!editingCustomer?.id) return;
        if (!editingCustomer?.email) {
            alert('This customer does not have an email address. Please add one before sending a review request.');
            return;
        }
        if (reviewCooldownDays) {
            alert(`A review request was already sent ${reviewCooldownDays.daysSince} day(s) ago. Please wait ${reviewCooldownDays.daysLeft} more day(s).`);
            return;
        }
        if (!window.confirm(`Send a Google review request to ${editingCustomer.email}?`)) return;
        setIsRequestingReview(true);
        try {
            const sendRequest = httpsCallable(functions, 'sendReviewRequest');
            await sendRequest({ customerId: editingCustomer.id });
            alert('Review request sent successfully!');
        } catch (error) {
            alert(`Failed to send review request: ${error.message}`);
        } finally {
            setIsRequestingReview(false);
        }
    };

    const TabButton = ({ id, label, icon: Icon }) => (
        <button
            id={`tab-${id}`} 
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === id
                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
        >
            <Icon size={16} /> {label}
        </button>
    );

    const sortedHistory = useMemo(() => {
        const camps = campaignHistory.map(c => ({
            id: c.id, contactDate: c.sentAt?.toDate(), contactType: 'Email Campaign', 
            noteText: `Sent campaign: "${c.subject}"`, isCampaign: true
        }));
        const completedJobs = customerJobs
            .filter(j => j.status === 'Completed' && j.endAt)
            .map(j => ({
                id: j.id,
                contactDate: j.endAt?.toDate ? j.endAt.toDate() : j.endAt,
                contactType: 'Job Completed',
                noteText: j.title,
                jobNumber: j.jobNumber,
                isJob: true,
                jobResource: j,
            }));
        // ROUND 4c VERIFIER: 1761980000-round4c-imported-history-timeline
        // Imported deal history entries — surface past Pipedrive activity in
        // the Timeline alongside notes/jobs/campaigns. Body string mirrors
        // Job Completed style: single text-blob, no special rendering.
        const importedHistory = customerImportedDeals.map(d => {
            const dealDate = d.closeDate?.toDate ? d.closeDate.toDate() : d.closeDate;
            // Body parts: "Pipedrive: <Stage>" + amount (if non-zero) + lead source (if any)
            const parts = [`Pipedrive: ${d.stage || 'Unknown stage'}`];
            if (canViewFinancials && d.amount && d.amount !== 0) {
                parts[0] += ` — $${Number(d.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            }
            if (d.leadSource) {
                parts.push(`Lead source: ${d.leadSource}`);
            }
            return {
                id: d.id,
                contactDate: dealDate,
                contactType: 'Imported History',
                noteText: parts.join('. '),
                isImportedHistory: true,
                importedDealResource: d,
            };
        });
        return [...contactHistory, ...camps, ...completedJobs, ...importedHistory]
            .sort((a, b) => (b.contactDate || 0) - (a.contactDate || 0));
    }, [contactHistory, campaignHistory, customerJobs, customerImportedDeals, canViewFinancials]);

    // ROUND 3c VERIFIER: 1761744000-round3c-fixH-sorted-invoices
    // Derived sorted invoice list for the Invoices table.
    const sortedCustomerInvoices = useMemo(() => {
        const arr = [...customerInvoices];
        const dir = invoiceSortDir === 'asc' ? 1 : -1;
        const toDate = (v) => {
            if (!v) return 0;
            if (v.toDate) return v.toDate().getTime();
            const d = new Date(v);
            return isNaN(d.getTime()) ? 0 : d.getTime();
        };
        arr.sort((a, b) => {
            switch (invoiceSortBy) {
                case 'number': {
                    const an = parseInt(a.invoiceNumber, 10) || 0;
                    const bn = parseInt(b.invoiceNumber, 10) || 0;
                    return (an - bn) * dir;
                }
                case 'date':
                    return (toDate(a.createdAt) - toDate(b.createdAt)) * dir;
                case 'status':
                    return ((a.status || '').localeCompare(b.status || '')) * dir;
                case 'amount':
                    return ((Number(a.total) || 0) - (Number(b.total) || 0)) * dir;
                default:
                    return 0;
            }
        });
        return arr;
    }, [customerInvoices, invoiceSortBy, invoiceSortDir]);

    // ROUND 3c VERIFIER: 1761744000-round3c-fixF-payment-ledger
    // Merged payment ledger: imported payments (from Round 3a importedPayments
    // subcollection) + live payments (extracted from each invoice's
    // paymentHistory array). Each entry is normalized to a common shape so
    // the ledger table can render uniformly.
    const mergedPaymentLedger = useMemo(() => {
        const ledger = [];

        // Imported payments
        for (const p of customerImportedPayments) {
            const dateMs = p.date?.toDate
                ? p.date.toDate().getTime()
                : (p.date ? new Date(p.date).getTime() : 0);
            ledger.push({
                key: `imp_${p.id}`,
                source: 'imported',
                date: isNaN(dateMs) ? 0 : dateMs,
                method: 'Imported invoice payment',
                reference: p.reference || '',
                invoiceNumber: null,
                invoiceId: null,
                amount: Number(p.amount) || 0,
                isRefund: !!p.isRefund,
                syncStatus: 'imported',
            });
        }

        // Live payments (embedded in invoice paymentHistory arrays)
        for (const inv of customerInvoices) {
            if (!Array.isArray(inv.paymentHistory) || inv.paymentHistory.length === 0) continue;
            for (let idx = 0; idx < inv.paymentHistory.length; idx++) {
                const p = inv.paymentHistory[idx];
                // netrev-6f2a8c53 -- the internal +credit-note line is not money from the customer:
                // it never shows here and never counts in Total Paid (its refund line does).
                if (isCreditNoteLine(p)) continue;
                const dateMs = p.date
                    ? (typeof p.date === 'string' ? new Date(p.date).getTime() : (p.date.toDate ? p.date.toDate().getTime() : new Date(p.date).getTime()))
                    : 0;
                ledger.push({
                    key: `live_${inv.id}_${idx}`,
                    source: 'live',
                    date: isNaN(dateMs) ? 0 : dateMs,
                    method: p.method || 'Payment',
                    reference: p.reference || '',
                    invoiceNumber: inv.invoiceNumber || null,
                    invoiceId: inv.id,
                    amount: Number(p.amount) || 0,
                    // sdrefund3-cfm-4b9a2e7d -- a LIVE refund entry (isRefund flag or a
                    // negative amount) must show the orange Refund badge too, not be
                    // force-flagged false. The outstanding sum already excludes !p.isRefund.
                    isRefund: !!p.isRefund || Number(p.amount) < 0,
                    syncStatus: p.syncError ? 'failed' : (p.qbPaymentId && p.qbPaymentId !== 'skipped' ? 'synced' : 'local'),
                });
            }
        }

        // SEAM 1: status-settled invoices carry no paymentHistory entry (owner
        // marked them Paid via the dropdown). Surface the settled amount so Total
        // Paid and the ledger reflect money recorded outside a payment event.
        // Marker-gated (settledViaStatus) so imported-Paid invoices — which are
        // tracked via importedPayments and share the same money shape — are never
        // double-counted here.
        for (const inv of customerInvoices) {
            if (!inv.settledViaStatus) continue;
            const amt = Number(inv.amountPaid) || 0;
            if (amt <= 0) continue;
            const dRaw = inv.settledAt || inv.lastPaymentDate;
            const dateMs = dRaw
                ? (typeof dRaw === 'string' ? new Date(dRaw).getTime() : (dRaw.toDate ? dRaw.toDate().getTime() : new Date(dRaw).getTime()))
                : 0;
            ledger.push({
                key: `settled_${inv.id}`,
                source: 'settled',
                date: isNaN(dateMs) ? 0 : dateMs,
                method: 'Marked Paid',
                reference: '',
                invoiceNumber: inv.invoiceNumber || null,
                invoiceId: inv.id,
                amount: amt,
                isRefund: false,
                syncStatus: 'local',
            });
        }

        // Apply user sort (default: date desc)
        const dir = paymentSortDir === 'asc' ? 1 : -1;
        ledger.sort((a, b) => {
            switch (paymentSortBy) {
                case 'date': return (a.date - b.date) * dir;
                case 'method': return a.method.localeCompare(b.method) * dir;
                case 'reference': return a.reference.localeCompare(b.reference) * dir;
                case 'invoice': {
                    const an = parseInt(a.invoiceNumber, 10) || 0;
                    const bn = parseInt(b.invoiceNumber, 10) || 0;
                    return (an - bn) * dir;
                }
                case 'amount': return (a.amount - b.amount) * dir;
                default: return 0;
            }
        });
        return ledger;
    }, [customerImportedPayments, customerInvoices, paymentSortBy, paymentSortDir]);

    // ROUND 3c VERIFIER: 1761744000-round3c-fixF-payment-totals
    // Three summary numbers for the Payment History tab.
    const paymentTotals = useMemo(() => {
        const totalBilled = customerInvoices.reduce((s, inv) => s + (inv.creditedTotal > 0 ? netInvoiceTotal(inv) : (Number(inv.total) || 0)), 0); // netrev-6f2a8c53 -- less credit notes; Number(inv.total) || 0 as before otherwise
        const totalPaid = mergedPaymentLedger.reduce((s, p) => s + p.amount, 0);
        const outstanding = customerInvoices.filter(isInvoiceOwing).reduce((s, inv) => s + (Number(inv.balanceDue) || 0), 0); // x2bweb-99626d17 — owing invoices only
        // Disclaimer trigger: any imported payment without invoice linkage
        // (which is true for ALL imported payments — they don't carry invoice IDs).
        // We surface the note when imported orphan payments exist AND outstanding > 0,
        // because that's where the math becomes ambiguous in user-facing terms.
        const importedOrphanTotal = customerImportedPayments
            .filter(p => !p.isRefund)
            .reduce((s, p) => s + (Number(p.amount) || 0), 0);
        const showDisclaimer = importedOrphanTotal > 0 && outstanding > 0;
        return { totalBilled, totalPaid, outstanding, importedOrphanTotal, showDisclaimer };
    }, [customerInvoices, mergedPaymentLedger, customerImportedPayments]);

    // Seam 7 — total available account credit: sum of availableBalance across the
    // customer's OPEN credits (depleted/terminal carry ~0 and are excluded by the
    // status filter). Matches ApplyCreditModal's selectable predicate.
    const accountCredit = useMemo(
        () => round2(customerCredits
            .filter(c => c.status === 'open')
            .reduce((s, c) => s + (Number(c.availableBalance) || 0), 0)),
        [customerCredits]
    );

    // Seam 7 — id -> invoice map so the credit ledger can resolve an application's
    // invoiceId to its human invoice NUMBER with zero extra reads (customerInvoices
    // is already every one of this customer's invoices). Missing id -> "unavailable".
    const invoicesById = useMemo(() => {
        const m = {};
        customerInvoices.forEach(i => { m[i.id] = i; });
        return m;
    }, [customerInvoices]);

    const lastContacted = useMemo(() => {
        return contactHistory.length > 0 ? contactHistory[0].contactDate : null;
    }, [contactHistory]);

    const lastJobDate = useMemo(() => {
        const completed = customerJobs
            .filter(j => j.status === 'Completed' && j.endAt)
            .map(j => j.endAt?.toDate ? j.endAt.toDate() : j.endAt)
            .filter(Boolean);
        if (completed.length === 0) return null;
        return new Date(Math.max(...completed.map(d => d.getTime())));
    }, [customerJobs]);

    const healthScore = useMemo(() => {
        if (!editingCustomer) return null;
        const now = new Date();
        let score = 0;

        // Signal 1: Recency of contact (0–20 pts)
        if (lastContacted) {
            const daysSince = Math.floor((now - lastContacted) / (1000 * 60 * 60 * 24));
            if (daysSince <= 30)       score += 20;
            else if (daysSince <= 60)  score += 14;
            else if (daysSince <= 90)  score += 8;
        }

        // Signal 2: Recency of completed work (0–20 pts)
        if (lastJobDate) {
            const daysSince = Math.floor((now - lastJobDate) / (1000 * 60 * 60 * 24));
            if (daysSince <= 60)        score += 20;
            else if (daysSince <= 120)  score += 14;
            else if (daysSince <= 180)  score += 8;
        }

        // Signal 3: Payment behaviour (0–20 pts)
        // Only score if they have at least one invoice — no invoices = no data to judge
        if (customerInvoices.length > 0) {
            if (overdueBalance === 0) {
                score += 20;
            } else if (lifetimeValue > 0 && overdueBalance / lifetimeValue < 0.2) {
                score += 10;
            }
        }

        // Signal 4: Engagement depth — completed jobs (0–20 pts)
        const completedCount = customerJobs.filter(j => j.status === 'Completed').length;
        if (completedCount >= 5)       score += 20;
        else if (completedCount >= 3)  score += 14;
        else if (completedCount >= 1)  score += 8;

        // Signal 5: Open follow-up hygiene (0–20 pts)
        // Only score if they have at least one follow-up appointment on record
        const overdueFollowUps = customerAppointments.filter(a => {
            if (a.status === 'Completed' || a.status === 'Cancelled') return false;
            const d = a.startAt?.toDate ? a.startAt.toDate() : a.startAt;
            return d && d < now;
        }).length;
        if (customerAppointments.length > 0) {
            if (overdueFollowUps === 0)      score += 20;
            else if (overdueFollowUps === 1) score += 10;
        }

        return Math.min(100, score);
    }, [editingCustomer, lastContacted, lastJobDate, overdueBalance, lifetimeValue, customerJobs, customerAppointments, customerInvoices]);

    const healthScoreDisplay = useMemo(() => {
        if (healthScore === null) return null;
        if (healthScore >= 80) return { label: 'Healthy',         color: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200',  dot: 'bg-green-500'  };
        if (healthScore >= 50) return { label: 'Needs Attention', color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200',  dot: 'bg-amber-500'  };
        return                         { label: 'At Risk',         color: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200',    dot: 'bg-red-500'    };
    }, [healthScore]);

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-0 lg:p-4">
            <div 
                id="customer-modal-window" 
                className="bg-white w-full max-w-7xl h-[100dvh] lg:h-[90vh] rounded-none lg:rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200"
            >
                <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 bg-white sticky top-0 z-10 flex-shrink-0 min-w-0 w-full">
                    <div className="flex justify-between items-start w-full min-w-0">
                        <div className="min-w-0 flex-1 mr-3">
                            <h2 className="text-lg lg:text-xl font-bold text-gray-900 flex items-center gap-2 flex-wrap min-w-0">
                                <User className="text-blue-600 flex-shrink-0" size={22} />
                                <span className="truncate">{editingCustomer ? formData.company || `${formData.firstName} ${formData.lastName}` : 'New Customer'}</span>
                                {editingCustomer && <CustomerStatusBadge status={formData.status} />}
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5 truncate">{formData.email || 'No email provided'}</p>
                            <div className="relative flex flex-wrap items-center gap-1.5 mt-2">
                                    {formData.tags.map(tId => {
                                        const t = allTags.find(x => x.id === tId);
                                        if (!t) return null;
                                        const c = tagChipColor(t.color);
                                        return (
                                            <span key={tId} className="px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 border" style={{ backgroundColor: c + '11', borderColor: c + '66', color: c }}>
                                                {t.label}
                                                <button type="button" onClick={() => handleRemoveTag(tId)} className="opacity-60 hover:opacity-100"><X size={10}/></button>
                                            </span>
                                        );
                                    })}
                                    <div>
                                        <input
                                            type="text"
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onFocus={() => setIsTagInputFocused(true)}
                                            onBlur={() => setTimeout(() => setIsTagInputFocused(false), 200)}
                                            onKeyDown={handleTagInputKeyDown}
                                            placeholder="+ Tag"
                                            className="w-24 focus:w-44 transition-all p-1 px-2 border border-dashed rounded-full text-xs"
                                        />
                                        {tagInput && !allTags.some(t => t.label.toLowerCase() === tagInput.trim().toLowerCase()) && (
                                            <div className="absolute left-0 top-full z-50 w-80 max-w-[calc(100vw-3rem)] mt-1 bg-white border rounded shadow-lg p-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddTag(tagInput)}
                                                    className="w-full text-left text-sm text-blue-600 hover:bg-blue-50 p-2 rounded flex items-start gap-2 whitespace-normal break-words"
                                                >
                                                    <Plus size={14} /> Create new tag "<strong>{tagInput}</strong>"
                                                </button>
                                            </div>
                                        )}
                                        {isTagInputFocused && allTags.length > 0 && (
                                            <div className="absolute left-0 top-full z-50 w-80 max-w-[calc(100vw-3rem)] mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto overflow-x-hidden">
                                                {allTags
                                                    .filter(t => !formData.tags.includes(t.id) && t.label.toLowerCase().includes(tagInput.toLowerCase()))
                                                    .map(t => (
                                                        <button
                                                            key={t.id}
                                                            type="button"
                                                            onClick={() => handleAddTag(t.label)}
                                                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center justify-between gap-2 whitespace-normal break-words"
                                                        >
                                                            {t.label}
                                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tagChipColor(t.color) }}></span>
                                                        </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                            </div>
                        </div>

                        {editingCustomer && (
                            <div className="hidden lg:flex items-stretch border border-gray-200 rounded-lg overflow-hidden flex-shrink-0 mr-3 shadow-sm">
                                {canViewFinancials && (
                                    <div className="px-4 py-2 text-right border-r border-gray-200 bg-green-50">
                                        <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Lifetime Value</p>
                                        <p className="text-base font-bold text-green-600 font-mono">{formatCurrency(lifetimeValue)}</p>
                                    </div>
                                )}
                                <div className="px-4 py-2 text-right border-r border-gray-200">
                                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Last Contacted</p>
                                    <p className="text-base font-bold text-gray-700">
                                        {lastContacted ? lastContacted.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : <span className="text-gray-400">—</span>}
                                    </p>
                                </div>
                                <div className="px-4 py-2 text-right">
                                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Last Invoice</p>
                                    <p className="text-base font-bold text-gray-700">
                                        {lastJobDate ? lastJobDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : <span className="text-gray-400">—</span>}
                                    </p>
                                </div>
                                {healthScoreDisplay && (
                                    <div className={`px-4 py-2 text-right border-l border-gray-200 ${healthScoreDisplay.bg}`}>
                                        <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Health</p>
                                        <div className="flex items-center justify-end gap-1.5">
                                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${healthScoreDisplay.dot}`}></div>
                                            <p className={`text-base font-bold ${healthScoreDisplay.color}`}>{healthScore}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <button 
                            onClick={closeModal}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700 flex-shrink-0"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {editingCustomer && (
                        <div className="flex lg:hidden gap-2 mt-2 overflow-x-auto pb-1 w-full min-w-0 hide-scrollbar">
                            {canViewFinancials && (
                                <div className="flex-shrink-0 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-center">
                                    <p className="text-[9px] text-green-600 uppercase font-bold tracking-wide">LTV</p>
                                    <p className="text-sm font-bold text-green-700 font-mono">{formatCurrency(lifetimeValue)}</p>
                                </div>
                            )}
                            <div className="flex-shrink-0 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-center">
                                <p className="text-[9px] text-gray-500 uppercase font-bold tracking-wide">Last Contact</p>
                                <p className="text-sm font-bold text-gray-700">
                                    {lastContacted ? lastContacted.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                                </p>
                            </div>
                            <div className="flex-shrink-0 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-center">
                                <p className="text-[9px] text-gray-500 uppercase font-bold tracking-wide">Last Invoice</p>
                                <p className="text-sm font-bold text-gray-700">
                                    {lastJobDate ? lastJobDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                                </p>
                            </div>
                            {healthScoreDisplay && (
                                <div className={`flex-shrink-0 border rounded-lg px-3 py-1.5 text-center ${healthScoreDisplay.bg} ${healthScoreDisplay.border}`}>
                                    <p className={`text-[9px] uppercase font-bold tracking-wide ${healthScoreDisplay.color}`}>Health</p>
                                    <div className="flex items-center justify-center gap-1">
                                        <div className={`w-1.5 h-1.5 rounded-full ${healthScoreDisplay.dot}`}></div>
                                        <p className={`text-sm font-bold ${healthScoreDisplay.color}`}>{healthScore}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex flex-1 overflow-hidden min-w-0 w-full">
                    <div 
                        id="customer-left-sidebar" 
                        className="hidden lg:block w-[350px] lg:w-[400px] border-r border-gray-200 bg-white overflow-y-auto p-6 flex-shrink-0 min-w-0"
                    >
                        <form id="customer-form" onSubmit={handleSave} className="space-y-5">
                            <div className="text-center mb-6">
                                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-600 font-bold text-2xl">{formData.firstName?.[0]}{formData.lastName?.[0]}</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <input name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0" />
                                    <input name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0" />
                                </div>
                                <input name="company" placeholder="Company (Optional)" value={formData.company} onChange={handleChange} className="mt-2 p-2 border rounded text-sm w-full min-w-0" />
                            </div>

                            <div className="space-y-3">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Contact Details</h4>
                                <div className="relative">
                                    <User className="absolute top-2.5 left-2.5 text-gray-400" size={14} />
                                    <input name="email" type="email" placeholder="Email Address" value={formData.email} onChange={handleChange} className="pl-8 p-2 border rounded text-sm w-full min-w-0" />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <input name="phone" placeholder="(555) 555-5555" value={formData.phone} onChange={handleChange} maxLength={14} className="p-2 border rounded text-sm w-full min-w-0" />
                                    <input name="cell_phone" placeholder="(555) 555-5555" value={formData.cell_phone} onChange={handleChange} maxLength={14} className="p-2 border rounded text-sm w-full min-w-0" />
                                </div>
                            </div>

                            {renderAddressFieldset()}

                            {customFieldDefs.length > 0 && (
                                <div className="space-y-3 pt-3 border-t">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Additional Info</h4>
                                    {customFieldDefs.map((field) => (
                                        <div key={field.id}>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
                                            <input type={field.type} value={formData.customData?.[field.id] || ''} onChange={(e) => handleCustomDataChange(field.id, e.target.value)} className="w-full min-w-0 p-2 border border-gray-300 rounded text-sm" />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="space-y-3 pt-3 border-t">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Settings</h4>
                                <select name="status" value={formData.status} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0 bg-white">
                                    <option value="New">New</option>
                                    <option value="Contacted">Contacted</option>
                                    <option value="Proposal Sent">Proposal Sent</option>
                                    <option value="Closed">Closed</option>
                                    <option value="Lost">Lost</option>
                                    <option value="Active">Active</option>
                                </select>
                                {/* r1-seam0c-replabel-9c2d5b73 — who owns this customer was an
                                    unlabelled dropdown under a generic "Settings" heading. Seam 0c
                                    makes ownership visible instead of enforcing it by hiding rows,
                                    and the quote builder's credit line points back here, so the
                                    field has to be readable as "this customer's rep". */}
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Sales Rep</label>
                                    <select name="sales_person_id" value={formData.sales_person_id} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0 bg-white" disabled={!canAssignSalesRep}>
                                        <option value="">-- Assign Sales Rep --</option>
                                        {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                </div>
                            </div>

                        </form>
                        {/* taxexempt3-8c4f2a1d — outside the form: saved on its own, straight to the server */}
                        <div className="mt-5"><CustomerTaxExemptionSection orgId={currentUserProfile?.organizationId} customer={editingCustomer} companySettings={companySettings} currentUserProfile={currentUserProfile} idPrefix="cte-desktop" /></div>
                    </div>

                    <div className="flex-1 flex flex-col bg-gray-50/50 min-w-0 w-full">
                        <div className="flex border-b bg-white px-2 lg:px-6 overflow-x-auto w-full hide-scrollbar min-w-0">
                            <button
                                onClick={() => setActiveTab('details')}
                                className={`lg:hidden flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === 'details'
                                        ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <User size={16} /> Details
                            </button>
                            {/* PROPERTIES v1 — 9d1b7e40 */}
                            <TabButton id="properties" label="Properties" icon={Building2} />
                            <TabButton id="history" label="Timeline" icon={History} />
                            <TabButton id="notesMedia" label="Notes & Media" icon={StickyNote} />
                            <TabButton id="additionalNotes" label="Additional Notes" icon={ClipboardList} />
                            {/* quotelock-d5c9a2f6 */}
                            {/* Estimates are unsold pricing — hidden from techs, who hold no
                                VIEW_FINANCIALS, are redirected off /quotes and have no Estimates
                                nav row. Follows Payment History below, not the Invoices tab
                                (transactional records of completed work). */}
                            {canViewFinancials && <TabButton id="quotes" label={docWordPlural} icon={FileText} />} {/* r1-p1-docterm-cfm-d9a4e1c7 */}
                            <TabButton id="jobs" label="Jobs" icon={Calendar} />
                            <TabButton id="invoices" label="Invoices" icon={DollarSign} />
                            {/* ROUND 3c VERIFIER: 1761744000-round3c-fixE-payments-tab */}
                            {/* Payment History is aggregate financials — hidden from techs (no VIEW_FINANCIALS). */}
                            {canViewFinancials && <TabButton id="payments" label="Payment History" icon={Receipt} />}
                            <TabButton id="followup" label="Follow-Up" icon={MessageSquare} />
                            <TabButton id="scheduling" label="Scheduling" icon={CalendarDays} />
                        </div>

                        {editingCustomer && (
                            <div className="bg-amber-50 border-b border-amber-200 px-4 lg:px-6 py-3 flex-shrink-0 min-w-0 w-full">
                                <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className="w-1 h-4 bg-amber-400 rounded-full flex-shrink-0"></div>
                                            <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Next Action</p>
                                        </div>
                                        <textarea
                                            name="nextAction"
                                            value={formData.nextAction || ''}
                                            onChange={handleChange}
                                            placeholder="What's the next step with this customer?"
                                            rows={1}
                                            className="w-full px-3 py-2 text-sm bg-white border border-amber-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 placeholder-gray-400 min-w-0"
                                            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px'; }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleSaveNextAction}
                                        className="flex-shrink-0 mt-6 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
                                    >
                                        Update
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto p-4 lg:p-6 w-full min-w-0">
                            {activeTab === 'details' && (
                                <div className="space-y-5 p-0 sm:p-4">
                                    <form id="customer-form-mobile" onSubmit={handleSave} className="space-y-4">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 text-blue-600 font-bold text-lg">{formData.firstName?.[0]}{formData.lastName?.[0]}</div>
                                            <div className="flex-1 min-w-0 space-y-1.5">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <input name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0" />
                                                    <input name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0" />
                                                </div>
                                                <input name="company" placeholder="Company (Optional)" value={formData.company} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0" />
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Contact Details</h4>
                                            <div className="relative">
                                                <User className="absolute top-2.5 left-2.5 text-gray-400" size={14} />
                                                <input name="email" type="email" placeholder="Email Address" value={formData.email} onChange={handleChange} className="pl-8 p-2 border rounded text-sm w-full min-w-0" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <input name="phone" placeholder="(555) 555-5555" value={formData.phone} onChange={handleChange} maxLength={14} className="p-2 border rounded text-sm w-full min-w-0" />
                                                <input name="cell_phone" placeholder="(555) 555-5555" value={formData.cell_phone} onChange={handleChange} maxLength={14} className="p-2 border rounded text-sm w-full min-w-0" />
                                            </div>
                                        </div>
                                        {renderAddressFieldset()}
                                        {customFieldDefs.length > 0 && (
                                            <div className="space-y-3 pt-3 border-t">
                                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Additional Info</h4>
                                                {customFieldDefs.map((field) => (
                                                    <div key={field.id}>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
                                                        <input type={field.type} value={formData.customData?.[field.id] || ''} onChange={(e) => handleCustomDataChange(field.id, e.target.value)} className="w-full min-w-0 p-2 border border-gray-300 rounded text-sm" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="space-y-3 pt-3 border-t">
                                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide">Settings</h4>
                                            <select name="status" value={formData.status} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0 bg-white">
                                                <option value="New">New</option>
                                                <option value="Contacted">Contacted</option>
                                                <option value="Proposal Sent">Proposal Sent</option>
                                                <option value="Closed">Closed</option>
                                                <option value="Lost">Lost</option>
                                                <option value="Active">Active</option>
                                            </select>
                                            {/* r1-seam0c-replabel-9c2d5b73 — mobile twin of the desktop
                                                rep field above; keep the two in step. */}
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">Sales Rep</label>
                                                <select name="sales_person_id" value={formData.sales_person_id} onChange={handleChange} className="p-2 border rounded text-sm w-full min-w-0 bg-white" disabled={!canAssignSalesRep}>
                                                    <option value="">-- Assign Sales Rep --</option>
                                                    {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        {/* Scheduling Preferences — mobile */}
                                        <div className="pt-3 border-t flex gap-3">
                                            {canEditCustomer && (
                                                <button type="submit" className="flex-1 py-2.5 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 shadow-md text-sm">
                                                    Save Changes
                                                </button>
                                            )}
                                            <button type="button" onClick={closeModal} className="flex-1 py-2.5 border rounded text-gray-600 font-medium hover:bg-gray-50 text-sm">
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                    {/* taxexempt3-8c4f2a1d — the same section in the phone-width layout */}
                                    <div className="mt-4"><CustomerTaxExemptionSection orgId={currentUserProfile?.organizationId} customer={editingCustomer} companySettings={companySettings} currentUserProfile={currentUserProfile} idPrefix="cte-mobile" /></div>
                                </div>
                            )}

                            {/* PROPERTIES v1 — 4a6c2f9b */}
                            {activeTab === 'properties' && (
                                <div className="space-y-6">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800">Properties</h3>
                                            <p className="text-sm text-gray-500">
                                                Additional service locations under this customer — rentals, managed sites, or any address you service. Each has its own verified address.
                                            </p>
                                        </div>
                                        {editingCustomer?.id && (
                                            <button
                                                type="button"
                                                onClick={() => { setEditingProperty(null); setIsPropertyEditorOpen(true); }}
                                                className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg shadow-sm whitespace-nowrap"
                                            >
                                                <Plus size={16} /> Add property
                                            </button>
                                        )}
                                    </div>

                                    {!editingCustomer?.id ? (
                                        <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
                                            <Building2 size={28} className="mx-auto text-gray-300 mb-2" />
                                            <h4 className="text-sm font-medium text-gray-700">No properties yet</h4>
                                            <p className="text-xs text-gray-500 mt-1">Save the customer first to start adding properties.</p>
                                        </div>
                                    ) : properties.length === 0 ? (
                                        <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
                                            <Building2 size={28} className="mx-auto text-gray-300 mb-2" />
                                            <h4 className="text-sm font-medium text-gray-700">No properties yet</h4>
                                            <p className="text-xs text-gray-500 mt-1">Add a location above to book jobs against a specific site.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {properties.map(prop => (
                                                <div key={prop.id} className="flex items-start justify-between gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-gray-800 truncate">{prop.name || prop.address1 || 'Unnamed property'}</span>
                                                            {prop.addressVerified ? (
                                                                <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">
                                                                    <CheckCircle size={11} /> Verified
                                                                </span>
                                                            ) : (
                                                                <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                                                    <AlertTriangle size={11} /> Unverified
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-0.5 flex items-start gap-1">
                                                            <MapPin size={11} className="flex-shrink-0 mt-0.5" />
                                                            <span className="break-words">{prop.addressCanonical || [prop.address1, prop.city, prop.state, prop.zip].filter(Boolean).join(', ') || 'No address'}</span>
                                                        </div>
                                                        {(prop.contactName || prop.contactPhone || prop.contactEmail) && (
                                                            <div className="text-xs text-gray-400 mt-0.5 truncate">
                                                                {[prop.contactName, prop.contactPhone, prop.contactEmail].filter(Boolean).join(' · ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-shrink-0 flex items-center gap-1">
                                                        <button type="button" onClick={() => { setEditingProperty(prop); setIsPropertyEditorOpen(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit property"><Edit2 size={15} /></button>
                                                        <button type="button" onClick={() => handleDeleteProperty(prop.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete property"><Trash2 size={15} /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'history' && (() => {
                                const FILTERS = [
                                    { id: 'all',      label: 'All' },
                                    { id: 'notes',    label: 'Notes' },
                                    { id: 'jobs',     label: 'Jobs' },
                                    // tlfilter-5b8e2d61 — aggregate surface, so org-default word ("Estimates"/"Quotes")
                                    { id: 'estimates', label: docWordPlural },
                                    { id: 'followups',label: 'Follow-Ups' },
                                    { id: 'campaigns',label: 'Campaigns' },
                                    // cnote-fe-2c9d5abf — invoice-email timeline entries
                                    { id: 'invoices',  label: 'Invoices' },
                                    // ROUND 4c VERIFIER: 1761980000-round4c-imported-history-timeline
                                    { id: 'importedHistory', label: 'Imported History' },
                                ];
                                // tlfilter-5b8e2d61 — list and counts both use timelineCategory
                                const filteredHistory = sortedHistory.filter(note =>
                                    timelineFilter === 'all' || timelineCategory(note) === timelineFilter
                                );
                                return (
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="font-bold text-gray-700">Activity Log</h3>
                                            <button type="button" onClick={() => setIsNoteModalOpen(true)} className="flex items-center gap-2 text-sm text-blue-600 font-medium hover:bg-blue-50 px-3 py-1.5 rounded transition-colors"><Plus size={16} /> Add Note</button>
                                        </div>
                                        <div className="flex gap-1.5 flex-wrap">
                                            {FILTERS.map(f => (
                                                <button
                                                    key={f.id}
                                                    type="button"
                                                    onClick={() => setTimelineFilter(f.id)}
                                                    className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
                                                        timelineFilter === f.id
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : 'bg-white text-gray-500 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                                                    }`}
                                                >
                                                    {f.label}
                                                    {f.id !== 'all' && (
                                                        <span className="ml-1 opacity-70">
                                                            ({sortedHistory.filter(n => timelineCategory(n) === f.id).length})
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="space-y-3">
                                            {filteredHistory.map(note => (
                                                <div
                                                    key={note.id}
                                                    onClick={() =>
                                                        note.isJob ? onRequestJobStart(note.jobResource)
                                                        : note.isCampaign ? setViewingCampaign(campaignHistory.find(c => c.id === note.id))
                                                        : (note.relatedType === 'invoice' && note.relatedId)
                                                            ? (() => {
                                                                const inv = customerInvoices.find(i => i.id === note.relatedId);
                                                                if (inv) { setSelectedInvoice(inv); setActiveTab('invoices'); }
                                                                else setViewingNote(note);
                                                              })()
                                                        : setViewingNote(note)
                                                    }
                                                    className="bg-white p-4 rounded-lg border hover:border-blue-300 cursor-pointer shadow-sm transition-all"
                                                >
                                                    <div className="flex justify-between text-sm mb-1">
                                                        <span className="font-semibold text-gray-800">{note.contactType}</span>
                                                        <span className="text-gray-500">{note.contactDate?.toLocaleDateString()}</span>
                                                    </div>
                                                    <p className="text-gray-600 text-sm line-clamp-2">
                                                        {note.jobNumber ? `#${note.jobNumber} — ` : ''}{note.noteText}
                                                    </p>
                                                    {note.createdByName && (
                                                        <p className="text-xs text-gray-400 mt-1.5">By: {note.createdByName}</p>
                                                    )}
                                                </div>
                                            ))}
                                            {filteredHistory.length === 0 && (
                                                <p className="text-center text-gray-400 py-10">
                                                    {sortedHistory.length === 0 ? 'No history found.' : 'No entries match this filter.'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

                            {activeTab === 'notesMedia' && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800">Notes & Media</h3>
                                        <p className="text-sm text-gray-500">
                                            Internal notes for the team and customer-facing notes that can appear on invoices and the customer portal.
                                        </p>
                                    </div>

                                    {!editingCustomer?.id ? (
                                        <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
                                            <StickyNote size={28} className="mx-auto text-gray-300 mb-2" />
                                            <h4 className="text-sm font-medium text-gray-700">No notes yet</h4>
                                            <p className="text-xs text-gray-500 mt-1">Save the customer first to start adding notes.</p>
                                        </div>
                                    ) : (
                                        <>
                                            <NoteComposer
                                                attachedTo={[{ type: 'customer', id: editingCustomer.id }]}
                                                orgId={currentUserProfile?.organizationId}
                                                currentUserProfile={currentUserProfile}
                                                orgUsers={allUsers}
                                                placeholder="Add a note about this customer..."
                                            />
                                            <div className="space-y-2 pt-2">
                                                {notesLoading && customerNotes.length === 0 ? (
                                                    <p className="text-xs text-gray-400 italic text-center py-4">Loading notes…</p>
                                                ) : customerNotes.length === 0 ? (
                                                    <div className="text-center py-8">
                                                        <p className="text-sm text-gray-500">No notes yet — add the first one above.</p>
                                                    </div>
                                                ) : (
                                                    customerNotes.map(note => (
                                                        <NoteCard
                                                            key={note.id}
                                                            note={note}
                                                            currentUserProfile={currentUserProfile}
                                                            orgUsers={allUsers}
                                                            orgId={currentUserProfile?.organizationId}
                                                        />
                                                    ))
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {activeTab === 'additionalNotes' && (
                                <div className="space-y-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800">Additional Notes</h3>
                                            <p className="text-sm text-gray-500">
                                                Custom details and imported attributes.
                                            </p>
                                        </div>
                                        {!isMiscFormOpen && (
                                            <button 
                                                onClick={() => {
                                                    setEditingKey(null);
                                                    setMiscForm({ key: '', value: '' });
                                                    setIsMiscFormOpen(true);
                                                }}
                                                className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                                            >
                                                <Plus size={16} /> Add Field
                                            </button>
                                        )}
                                    </div>

                                    {isMiscFormOpen && (
                                        <div className="bg-gray-50 border border-indigo-100 p-4 rounded-lg mb-6 shadow-sm">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div className="md:col-span-1">
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Header (Label)</label>
                                                    <input 
                                                        type="text" 
                                                        value={miscForm.key}
                                                        onChange={(e) => setMiscForm(prev => ({ ...prev, key: e.target.value }))}
                                                        placeholder="e.g. Gate Code"
                                                        className="w-full min-w-0 text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Body (Content)</label>
                                                    <textarea 
                                                        value={miscForm.value}
                                                        onChange={(e) => setMiscForm(prev => ({ ...prev, value: e.target.value }))}
                                                        placeholder="Enter details..."
                                                        rows={1}
                                                        className="w-full min-w-0 text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => setIsMiscFormOpen(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded">Cancel</button>
                                                <button onClick={handleSaveMisc} className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"><Save size={16} /> Save Note</button>
                                            </div>
                                        </div>
                                    )}

                                    {liveAdditionalNotes && Object.keys(liveAdditionalNotes).length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {Object.entries(liveAdditionalNotes).map(([key, value]) => (
                                                <div key={key} className="group relative p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-indigo-300 transition-all min-w-0">
                                                    <div className="pr-16 min-w-0">
                                                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 truncate">{key}</div>
                                                        <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap break-words">
                                                            {value && typeof value === 'object' ? JSON.stringify(value) : value}
                                                        </div>
                                                    </div>
                                                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white pl-2">
                                                        <button onClick={() => handleEditMisc(key, value)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Edit"><Edit2 size={14} /></button>
                                                        <button onClick={() => handleDeleteMisc(key)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14} /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        !isMiscFormOpen && (
                                            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                                <ClipboardList className="mx-auto h-10 w-10 text-gray-300 mb-3" />
                                                <h3 className="text-sm font-medium text-gray-900">No Additional Notes</h3>
                                                <p className="text-sm text-gray-500 mt-1">Add custom fields for things like gate codes, spouse names, or preferences.</p>
                                                <button onClick={() => setIsMiscFormOpen(true)} className="mt-4 text-indigo-600 hover:text-indigo-800 text-sm font-medium">+ Add Your First Note</button>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}

                            {activeTab === 'invoices' && (
                                <>
                                    {selectedInvoice ? (
                                        <InvoiceDetailsView invoice={selectedInvoice} companySettings={companySettings} onBack={() => setSelectedInvoice(null)} onEditInvoice={openInvoiceModal} currentUserProfile={currentUserProfile} />
                                    ) : (
                                        <div>
                                            <div className="flex justify-between items-center mb-4">
                                                {/* Outstanding/Overdue are aggregate balances — hidden from techs. The
                                                    invoice list + amounts below stay visible (transactional records). */}
                                                {canViewFinancials && (
                                                    <div className="flex gap-4 text-sm flex-wrap">
                                                        <div className="bg-white px-3 py-1 rounded border shadow-sm">
                                                            <span className="text-gray-500 block text-xs">Outstanding</span>
                                                            <span className="font-bold text-gray-900">{formatCurrency(outstandingBalance)}</span>
                                                        </div>
                                                        {overdueBalance > 0 && (
                                                            <div className="bg-red-50 px-3 py-1 rounded border border-red-100 shadow-sm">
                                                                <span className="text-red-500 block text-xs">Overdue</span>
                                                                <span className="font-bold text-red-700">{formatCurrency(overdueBalance)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {/* New Invoice opens InvoiceFormModal, whose create rule requires
                                                    ownsCustomer (sales_person_id==uid || isHighLevel). isOwnerOrManager
                                                    mirrors that exactly, so hide the button from users the rules would
                                                    reject (incl. techs) rather than show a dead control. */}
                                                {isOwnerOrManager && (
                                                    <button onClick={() => openInvoiceModal({ customerId: editingCustomer.id })} className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-700 shadow-sm flex items-center gap-2 whitespace-nowrap"><Plus size={14}/> New Invoice</button>
                                                )}
                                            </div>
                                            <div className="bg-white rounded-lg border shadow-sm overflow-x-auto w-full">
                                                <table className="w-full text-sm text-left whitespace-nowrap">
                                                    <thead className="bg-gray-50 text-gray-500 font-medium border-b">
                                                        {/* ROUND 3c VERIFIER: 1761744000-round3c-fixH-sortable-invoices */}
                                                        <tr>
                                                            <SortableHeader label="Number" field="number" sortBy={invoiceSortBy} sortDir={invoiceSortDir} onSort={(f, d) => { setInvoiceSortBy(f); setInvoiceSortDir(d); }} />
                                                            <SortableHeader label="Date" field="date" sortBy={invoiceSortBy} sortDir={invoiceSortDir} onSort={(f, d) => { setInvoiceSortBy(f); setInvoiceSortDir(d); }} />
                                                            <SortableHeader label="Status" field="status" sortBy={invoiceSortBy} sortDir={invoiceSortDir} onSort={(f, d) => { setInvoiceSortBy(f); setInvoiceSortDir(d); }} />
                                                            <SortableHeader label="Amount" field="amount" sortBy={invoiceSortBy} sortDir={invoiceSortDir} onSort={(f, d) => { setInvoiceSortBy(f); setInvoiceSortDir(d); }} align="right" />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sortedCustomerInvoices.map(inv => (
                                                            <tr key={inv.id} onClick={() => setSelectedInvoice(inv)} className="border-b hover:bg-blue-50 cursor-pointer">
                                                                <td className="px-4 py-3 font-medium text-blue-600">#{inv.invoiceNumber}</td>
                                                                <td className="px-4 py-3">{inv.createdAt?.toDate ? inv.createdAt.toDate().toLocaleDateString() : (inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '-')}</td>
                                                                <td className="px-4 py-3"><InvoiceStatusBadge status={inv.status} /></td>
                                                                <td className="px-4 py-3 text-right font-medium">{formatCurrency(inv.total)}</td>
                                                            </tr>
                                                        ))}
                                                        {sortedCustomerInvoices.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-gray-400">No invoices yet.</td></tr>}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* quotelock-d5c9a2f6 — body gate matching the tab button above. */}
                            {canViewFinancials && activeTab === 'quotes' && <CustomerQuotesView customer={editingCustomer} currentUserProfile={currentUserProfile} openQuoteModal={openQuoteModal} companySettings={companySettings} onConvertToInvoice={onConvertToInvoice} onConvertToJob={onConvertToJob} onViewJob={onViewJob} />}

                            {/* ROUND 3c VERIFIER: 1761744000-round3c-fixF-payment-tab-gate */}
                            {/* Aggregate financials — techs lack VIEW_FINANCIALS, and the tab button
                                above is hidden from them too, so this never renders for a tech. */}
                            {canViewFinancials && activeTab === 'payments' && (
                                <div>
                                    {isOwnerOrManager && editingCustomer?.id && (
                                        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                                            {/* acctstmt-5b3e9f14 -- Seam 10: send a period statement (default last 12
                                                months) for ANY customer, including credit-only / all-paid accounts the
                                                Collections report (totalDue>0) never lists. Disabled when no email on file. */}
                                            <div className="flex flex-wrap items-end gap-2">
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">Statement from</label>
                                                    <input
                                                        type="date"
                                                        value={stmtStart}
                                                        onChange={(e) => setStmtStart(e.target.value)}
                                                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">to</label>
                                                    <input
                                                        type="date"
                                                        value={stmtEnd}
                                                        onChange={(e) => setStmtEnd(e.target.value)}
                                                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                                                    />
                                                </div>
                                                <button
                                                    onClick={handleSendStatement}
                                                    disabled={sendingStatement || !editingCustomer.email}
                                                    title={editingCustomer.email ? 'Email this customer their account statement' : 'No email on file'}
                                                    className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {sendingStatement ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Statement
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => setIsRecordPaymentOpen(true)}
                                                className="bg-green-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-green-700 shadow-sm flex items-center gap-2"
                                            >
                                                <Plus size={14} /> Record Payment
                                            </button>
                                        </div>
                                    )}
                                    <PaymentHistoryTab
                                        totals={paymentTotals}
                                        accountCredit={accountCredit}
                                        credits={customerCredits}
                                        ledgerEntriesByCredit={ledgerEntriesByCredit}
                                        expandedCreditId={expandedCreditId}
                                        loadingCreditId={loadingCreditId}
                                        onToggleCredit={handleToggleCredit}
                                        isOwnerOrManager={isOwnerOrManager}
                                        onSpreadCredit={(c) => setSpreadCredit(c)}
                                        invoicesById={invoicesById}
                                        ledger={mergedPaymentLedger}
                                        sortBy={paymentSortBy}
                                        sortDir={paymentSortDir}
                                        onSort={(f, d) => { setPaymentSortBy(f); setPaymentSortDir(d); }}
                                        onInvoiceClick={(invoiceId) => {
                                            const inv = customerInvoices.find(i => i.id === invoiceId);
                                            if (inv) { setSelectedInvoice(inv); setActiveTab('invoices'); }
                                        }}
                                    />
                                </div>
                            )}

                            {activeTab === 'jobs' && (
                                <div>
                                    <div className="flex justify-end mb-4">
                                        <button onClick={() => openJobEditorModal({ clientId: editingCustomer.id, clientName: editingCustomer.name, address: [editingCustomer.address1, editingCustomer.city].filter(Boolean).join(', ') })} className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-700 shadow-sm flex items-center gap-2"><Plus size={14}/> New Job</button>
                                    </div>
                                    <EventList events={customerJobs} onEventClick={onRequestJobStart} />
                                </div>
                            )}


                            {activeTab === 'scheduling' && (
                                <div className="space-y-6 max-w-xl">
                                    <div>
                                        <h3 className="text-base font-semibold text-gray-800 mb-1">Scheduling Preferences</h3>
                                        <p className="text-sm text-gray-500 mb-5">Optional — used by the Weekly Planner to honour this customer's availability. Nothing here is mandatory.</p>

                                        {/* Preferred Days */}
                                        <div className="mb-5">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Days</label>
                                            <div className="flex flex-wrap gap-2">
                                                {['monday','tuesday','wednesday','thursday','friday','saturday'].map(day => {
                                                    const isSelected = (formData.preferredDays || []).includes(day);
                                                    return (
                                                        <button
                                                            key={day}
                                                            type="button"
                                                            onClick={() => {
                                                                const current = formData.preferredDays || [];
                                                                setFormData(p => ({
                                                                    ...p,
                                                                    preferredDays: isSelected
                                                                        ? current.filter(d => d !== day)
                                                                        : [...current, day]
                                                                }));
                                                            }}
                                                            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors capitalize ${
                                                                isSelected
                                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                                    : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                                                            }`}
                                                        >
                                                            {day.charAt(0).toUpperCase() + day.slice(1)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-xs text-gray-400 mt-2">The weekly planner will try to assign this customer to one of their preferred days.</p>
                                        </div>

                                        {/* Preferred Time Window */}
                                        <div className="mb-5">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Arrival Time</label>
                                            <div className="flex rounded-lg overflow-hidden border border-gray-300 w-fit">
                                                {[['anytime','Any Time'],['morning','Morning (8am–12pm)'],['afternoon','Afternoon (12pm–5pm)']].map(([val, label]) => (
                                                    <button
                                                        key={val}
                                                        type="button"
                                                        onClick={() => setFormData(p => ({ ...p, preferredWindow: val }))}
                                                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                                                            formData.preferredWindow === val
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-white text-gray-600 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Preferred Crew */}
                                        {routeGroups.length > 0 && (
                                            <div className="mb-5">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Crew</label>
                                                <select
                                                    value={formData.preferredCrewId || ''}
                                                    onChange={e => setFormData(p => ({ ...p, preferredCrewId: e.target.value }))}
                                                    className="w-full max-w-xs p-2.5 border rounded-lg text-sm bg-white"
                                                >
                                                    <option value="">No preference — assign automatically</option>
                                                    {routeGroups.map(g => (
                                                        <option key={g.id} value={g.id}>{g.name}</option>
                                                    ))}
                                                </select>
                                                <p className="text-xs text-gray-400 mt-1.5">When set, the Weekly Planner will always assign this customer to this crew. Leave blank to let the algorithm decide based on geography.</p>
                                            </div>
                                        )}

                                        {/* Default Job Duration */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Default Job Duration</label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="15"
                                                    value={formData.estimatedJobDuration || ''}
                                                    onChange={e => setFormData(p => ({ ...p, estimatedJobDuration: e.target.value ? parseInt(e.target.value) : '' }))}
                                                    placeholder="e.g. 60"
                                                    className="w-28 p-2.5 border rounded-lg text-sm"
                                                />
                                                <span className="text-sm text-gray-500">minutes</span>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1.5">Pre-fills the duration field in the Weekly Planner. You can always override it per-week at planning time.</p>
                                        </div>

                                        {/* ── Customer Sticky Note ── */}
                                        <div className="mb-6 pt-6 border-t border-gray-100">
                                            <div className="mb-3">
                                                <label className="block text-sm font-semibold text-gray-800 mb-0.5">
                                                    Customer Note
                                                </label>
                                                <p className="text-xs text-gray-400">
                                                    A note specific to this customer — e.g. "Don't cut beside the pool. Gate code is 4821." Automatically appended to jobs and/or invoices based on the toggles below. This is separate from the template's default notes, which describe the type of job.
                                                </p>
                                            </div>

                                            <textarea
                                                rows={3}
                                                value={formData.jobNote || ''}
                                                onChange={e => setFormData(p => ({ ...p, jobNote: e.target.value }))}
                                                placeholder="e.g. Don't cut beside the pool area. Gate code is 4821. Dog is friendly."
                                                className="w-full p-3 border border-gray-300 rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-500 outline-none"
                                            />

                                            {/* Append toggles */}
                                            <div className="mt-3 space-y-2">
                                                {/* Append to Jobs */}
                                                <label className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                                                    formData.jobNoteEnabled
                                                        ? 'bg-blue-50 border-blue-200'
                                                        : 'bg-white border-gray-200 hover:border-gray-300'
                                                }`}>
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${formData.jobNoteEnabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                                            <Calendar size={16} className={formData.jobNoteEnabled ? 'text-blue-600' : 'text-gray-400'} />
                                                        </div>
                                                        <div>
                                                            <p className={`text-sm font-semibold ${formData.jobNoteEnabled ? 'text-blue-800' : 'text-gray-700'}`}>
                                                                Append to every job
                                                            </p>
                                                            <p className="text-xs text-gray-400">Note appears in the job description each time a job is created for this customer</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(p => ({ ...p, jobNoteEnabled: !p.jobNoteEnabled }))}
                                                        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors flex-shrink-0 ${
                                                            formData.jobNoteEnabled ? 'bg-blue-600' : 'bg-gray-300'
                                                        }`}
                                                    >
                                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                                            formData.jobNoteEnabled ? 'translate-x-5' : 'translate-x-0'
                                                        }`} />
                                                    </button>
                                                </label>

                                                {/* Append to Invoices */}
                                                <label className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                                                    formData.invoiceNoteEnabled
                                                        ? 'bg-green-50 border-green-200'
                                                        : 'bg-white border-gray-200 hover:border-gray-300'
                                                }`}>
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${formData.invoiceNoteEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                                                            <DollarSign size={16} className={formData.invoiceNoteEnabled ? 'text-green-600' : 'text-gray-400'} />
                                                        </div>
                                                        <div>
                                                            <p className={`text-sm font-semibold ${formData.invoiceNoteEnabled ? 'text-green-800' : 'text-gray-700'}`}>
                                                                Append to every invoice
                                                            </p>
                                                            <p className="text-xs text-gray-400">Note appears in the invoice memo field for this customer's invoices</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(p => ({ ...p, invoiceNoteEnabled: !p.invoiceNoteEnabled }))}
                                                        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors flex-shrink-0 ${
                                                            formData.invoiceNoteEnabled ? 'bg-green-600' : 'bg-gray-300'
                                                        }`}
                                                    >
                                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                                            formData.invoiceNoteEnabled ? 'translate-x-5' : 'translate-x-0'
                                                        }`} />
                                                    </button>
                                                </label>
                                            </div>

                                            {(formData.jobNoteEnabled || formData.invoiceNoteEnabled) && !formData.jobNote?.trim() && (
                                                <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
                                                    <AlertTriangle size={12} />
                                                    <span>Write a note above before enabling — currently nothing will be appended.</span>
                                                </div>
                                            )}
                                        </div>

                                        {canEditCustomer && (
                                            <button
                                                type="submit"
                                                form="customer-form"
                                                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 shadow-sm"
                                            >
                                                Save Preferences
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'followup' && (() => {
                                const now = new Date();
                                const upcomingEvents = customerAppointments
                                    .filter(e => e.status !== 'Completed' && e.status !== 'Cancelled')
                                    .sort((a, b) => (a.startAt?.toDate() || 0) - (b.startAt?.toDate() || 0));
                                const historyEvents = customerAppointments
                                    .filter(e => e.status === 'Completed' || e.status === 'Cancelled')
                                    .sort((a, b) => (b.startAt?.toDate() || 0) - (a.startAt?.toDate() || 0));
                                return (
                                    <div className="space-y-6">
                                        <div className="bg-white p-5 rounded border shadow-sm">
                                            <h4 className="font-bold text-gray-800 mb-2">Follow-Up Automation</h4>
                                            <p className="text-sm text-gray-600 mb-4">Select an automated schedule for this client.</p>
                                            <div className="flex gap-2">
                                                <select name="followUpSchedule" value={formData.followUpSchedule} onChange={handleChange} className="p-2 border rounded text-sm flex-1 min-w-0">
                                                    <option value="None">None</option>
                                                    <option value="Light">Light</option>
                                                    <option value="Moderate">Moderate</option>
                                                    <option value="Aggressive">Aggressive</option>
                                                </select>
                                                <button onClick={handleSaveSchedule} className="bg-blue-600 text-white px-4 rounded text-sm hover:bg-blue-700" disabled={isScheduleSaving}>{isScheduleSaving ? 'Saving...' : 'Save'}</button>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex justify-between items-center mb-3">
                                                <h4 className="font-bold text-gray-800">Upcoming</h4>
                                                <button
                                                    type="button"
                                                    onClick={() => openJobEditorModal({ clientId: editingCustomer.id, clientName: editingCustomer.name, eventType: 'Follow-Up' })}
                                                    className="flex items-center gap-1.5 text-sm text-blue-600 font-medium hover:bg-blue-50 px-3 py-1.5 rounded transition-colors border border-blue-200"
                                                >
                                                    <Plus size={14} /> Schedule Follow-Up
                                                </button>
                                            </div>
                                            {upcomingEvents.length === 0 ? (
                                                <p className="text-center text-gray-400 py-6 text-sm">No upcoming follow-ups.</p>
                                            ) : (
                                                <div className="space-y-3">
                                                    {upcomingEvents.map(event => {
                                                        const eventDate = event.startAt?.toDate();
                                                        const isOverdue = eventDate && eventDate < now;
                                                        return (
                                                            <div
                                                                key={event.id}
                                                                onClick={() => openJobEditorModal(event)}
                                                                className={`p-4 rounded-lg border cursor-pointer flex justify-between items-center transition-colors ${
                                                                    isOverdue
                                                                        ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                                                                        : 'bg-white border-gray-200 hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                <div>
                                                                    <div className="flex items-center gap-2">
                                                                        {isOverdue && <AlertCircle size={14} className="text-amber-500 flex-shrink-0" />}
                                                                        <p className="font-semibold text-gray-800 text-sm">{event.title}</p>
                                                                    </div>
                                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                                        {eventDate ? eventDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'No Date'}
                                                                        {event.eventType && ` · ${event.eventType}`}
                                                                        {isOverdue && <span className="text-amber-600 font-medium"> · Overdue</span>}
                                                                    </p>
                                                                </div>
                                                                <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${isOverdue ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                                                                    {event.status}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {historyEvents.length > 0 && (
                                            <div>
                                                <h4 className="font-bold text-gray-500 mb-3 text-sm uppercase tracking-wide">History</h4>
                                                <div className="space-y-2">
                                                    {historyEvents.map(event => {
                                                        const eventDate = event.startAt?.toDate();
                                                        const isDone = event.status === 'Completed';
                                                        return (
                                                            <div
                                                                key={event.id}
                                                                onClick={() => openJobEditorModal(event)}
                                                                className="p-3 rounded-lg border border-gray-100 bg-gray-50 cursor-pointer hover:bg-gray-100 flex justify-between items-center opacity-70 transition-colors"
                                                            >
                                                                <div>
                                                                    <div className="flex items-center gap-2">
                                                                        <CheckCircle size={13} className={isDone ? 'text-green-500 flex-shrink-0' : 'text-gray-400 flex-shrink-0'} />
                                                                        <p className="text-sm text-gray-600">{event.title}</p>
                                                                    </div>
                                                                    <p className="text-xs text-gray-400 mt-0.5 pl-5">
                                                                        {eventDate ? eventDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No Date'}
                                                                        {event.completedByName && ` · Completed by ${event.completedByName}`}
                                                                    </p>
                                                                </div>
                                                                <span className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${isDone ? 'bg-green-50 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                                                                    {event.status}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                {/* --- RESPONSIVE BOTTOM FOOTER ACTION BAR --- */}
                <div className="bg-white border-t p-3 lg:p-4 flex flex-col sm:flex-row justify-between items-center gap-3 flex-shrink-0 w-full min-w-0 z-10">
                    <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-center sm:justify-start">
                        {editingCustomer && (
                            <>
                                {/* s8portal-4e7b2c95 — hidden from roles that may neither email an invite nor copy a link (Field Technicians) */}
                                {(canSendPortalInvite(currentUserProfile) || canCreatePortalLink(currentUserProfile)) && (
                                <button type="button" onClick={() => setIsInviteModalOpen(true)} className="text-purple-600 bg-purple-50 hover:bg-purple-100 px-3 py-2 rounded font-medium text-sm flex items-center justify-center gap-2 flex-1 sm:flex-none whitespace-nowrap"><Key size={16} /> <span className="hidden xs:inline">Portal</span> Access</button>
                                )}
                                <button type="button" onClick={() => onComposeRequest(editingCustomer)} className="text-green-600 bg-green-50 hover:bg-green-100 px-3 py-2 rounded font-medium text-sm flex items-center justify-center gap-2 flex-1 sm:flex-none whitespace-nowrap" disabled={!isEmailConnected}><Send size={16} /> Email</button>
                                {editingCustomer?.email && (
                                    <button
                                        type="button"
                                        onClick={handleRequestReview}
                                        disabled={isRequestingReview || !!reviewCooldownDays}
                                        title={reviewCooldownDays ? `Review requested ${reviewCooldownDays.daysSince}d ago — available again in ${reviewCooldownDays.daysLeft}d` : 'Request a Google review from this customer'}
                                        className={`px-3 py-2 rounded font-medium text-sm flex items-center justify-center gap-2 flex-1 sm:flex-none whitespace-nowrap transition-colors ${
                                            reviewCooldownDays
                                                ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                                                : 'text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                                        }`}
                                    >
                                        <Star size={15} />
                                        {isRequestingReview ? 'Sending...' : reviewCooldownDays ? `Sent ${reviewCooldownDays.daysSince}d ago` : 'Review'}
                                    </button>
                                )}
                                {highLevelRoles.includes(currentUserProfile?.role) && (
                                    <button type="button" onClick={() => onDeleteRequest(editingCustomer.id)} className="text-red-600 hover:bg-red-50 px-3 py-2 rounded font-medium text-sm flex-1 sm:flex-none whitespace-nowrap text-center">Delete</button>
                                )}
                            </>
                        )}
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                        <button type="button" onClick={closeModal} className="flex-1 sm:flex-none px-4 py-2 border rounded text-gray-600 font-medium hover:bg-gray-50 whitespace-nowrap">Cancel</button>
                        {canEditCustomer && (
                            <button type="submit" form="customer-form" className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 shadow-md whitespace-nowrap text-center">Save Changes</button>
                        )}
                    </div>
                </div>
            </div>

            {isNoteModalOpen && <AddNoteModal isOpen={isNoteModalOpen} onClose={() => setIsNoteModalOpen(false)} user={user} customerId={editingCustomer.id} currentStatus={formData.status} onStatusChange={handleStatusUpdateFromNote} currentUserProfile={currentUserProfile} />}
            {viewingNote && <ViewNoteModal isOpen={!!viewingNote} onClose={() => setViewingNote(null)} note={viewingNote} />}
            {viewingCampaign && <CampaignDetailsModal campaign={viewingCampaign} onClose={() => setViewingCampaign(null)} />}
            {isInviteModalOpen && <PortalInviteModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} customer={editingCustomer} orgId={currentUserProfile?.organizationId} currentUserProfile={currentUserProfile} />}
            {/* PROPERTIES v1 — e83d51a7 */}
            {isPropertyEditorOpen && (
                <PropertyEditorModal
                    editingProperty={editingProperty}
                    defaultCountry={defaultCountry}
                    onClose={() => { setIsPropertyEditorOpen(false); setEditingProperty(null); }}
                    onSave={handleSaveProperty}
                    customers={customers || []} // billto-cfm-pass-7c1d4f82 -- third-party dropdown data for the default bill-to
                    currentCustomerId={editingCustomer?.id || null}
                />
            )}
            
            <ConfirmationModal isOpen={!!duplicate} onClose={() => setDuplicate(null)} onConfirm={saveCustomerData} title="Duplicate Customer?" message="A customer with similar details exists." confirmText="Create Anyway" />

            {/* payoffl-4a1c7e93 -- Seam 11 Part C: pass the customer's live invoices so the
                modal's "apply to open invoices" preview can show which invoices the payment
                will clear (oldest first). The tab's snapshots refresh the balances on their
                own after the spread, so no onSuccess plumbing is needed. */}
            {isRecordPaymentOpen && (
                <RecordAccountPaymentModal
                    isOpen={isRecordPaymentOpen}
                    onClose={() => setIsRecordPaymentOpen(false)}
                    customer={editingCustomer}
                    invoices={customerInvoices}
                />
            )}

            {/* Seam 15 -- spread an existing credit. Same live customerInvoices the
                Record Payment preview uses; the customerCredits + customerInvoices
                snapshots refresh balances after the spread, so no onSuccess plumbing
                is needed beyond closing the modal. */}
            {spreadCredit && (
                <SpreadCreditModal
                    isOpen={!!spreadCredit}
                    onClose={() => setSpreadCredit(null)}
                    credit={spreadCredit}
                    invoices={customerInvoices}
                />
            )}
        </div>
    );
};

const EventList = ({ events, onEventClick }) => {
    if (!events || events.length === 0) return <p className="text-center text-gray-500 py-4">No events found.</p>;
    return (
        <div className="space-y-3">
            {events.map(event => (
                <div key={event.id} onClick={() => onEventClick(event)} className="p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 cursor-pointer flex justify-between items-center">
                    <div className="min-w-0 pr-4">
                        <p className="font-semibold text-gray-800 truncate">{event.title}</p>
                        <p className="text-sm text-gray-500 truncate">{event.startAt?.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) || 'No Date'} {event.eventType && ` (${event.eventType})`}</p>
                    </div>
                    <div className="text-sm font-medium text-gray-700 whitespace-nowrap">{event.status}</div>
                </div>
            ))}
        </div>
    );
};

// ============================================================================
// ROUND 3c VERIFIER: 1761744000-round3c-fixG-sortable-header
// Reusable sortable table header. Click toggles sort direction; clicking a
// different column resets to descending. Used in both Invoices table and
// Payment History ledger.
// ============================================================================
const SortableHeader = ({ label, field, sortBy, sortDir, onSort, align = 'left', className = '' }) => {
    const isActive = sortBy === field;
    const handleClick = () => {
        if (isActive) {
            onSort(field, sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            onSort(field, 'desc');
        }
    };
    const Icon = isActive ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    const alignClass = align === 'right' ? 'text-right justify-end' : 'text-left';
    return (
        <th
            onClick={handleClick}
            className={`px-4 py-3 cursor-pointer select-none hover:bg-gray-100 transition-colors ${align === 'right' ? 'text-right' : ''} ${className}`}
        >
            <span className={`inline-flex items-center gap-1.5 ${alignClass} ${isActive ? 'text-gray-700 font-semibold' : 'text-gray-500'}`}>
                {label}
                <Icon size={12} className={isActive ? 'text-gray-700' : 'text-gray-400'} />
            </span>
        </th>
    );
};

// ============================================================================
// ROUND 3c VERIFIER: 1761744000-round3c-fixF-payment-history-tab
// Payment History tab: customer-level AR ledger merging imported payments
// (from Round 3a customers/{cid}/importedPayments subcollection) with live
// payments (extracted from each invoice's paymentHistory array). Three
// summary cards at top, conditional disclaimer for orphan-payment ambiguity,
// sortable ledger table.
// ============================================================================
// Seam 7 — presentation helpers for the account-credit history (Part B).
const CREDIT_STATUS_STYLES = {
    open: 'bg-emerald-100 text-emerald-800',
    depleted: 'bg-gray-100 text-gray-600',
    refunded: 'bg-orange-100 text-orange-800',
    writtenOff: 'bg-gray-200 text-gray-700',
    voided: 'bg-gray-200 text-gray-700',
};
// creditSourceLabel lifted to src/utils/formatters.js (shared with ApplyCreditModal).
// Firestore Timestamp | ms | Date -> localized date string (or em dash).
const fmtCreditDate = (ts) => {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return isNaN(d?.getTime?.()) ? '—' : d.toLocaleDateString();
};
const tsMillis = (ts) => {
    if (!ts) return 0;
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    const t = d?.getTime?.();
    return Number.isFinite(t) ? t : 0;
};

const PaymentHistoryTab = ({ totals, accountCredit = 0, credits = [], ledgerEntriesByCredit = {}, expandedCreditId, loadingCreditId, onToggleCredit, isOwnerOrManager = false, onSpreadCredit, invoicesById = {}, ledger, sortBy, sortDir, onSort, onInvoiceClick }) => {
    const formatLedgerDate = (ms) => {
        if (!ms) return '-';
        const d = new Date(ms);
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString();
    };

    // Applications/reversals store invoiceId only — resolve the human number from
    // the in-memory invoice map (zero extra reads). Missing -> "unavailable".
    const invLabel = (invoiceId) => {
        if (!invoiceId) return 'invoice';
        const inv = invoicesById[invoiceId];
        if (inv) return inv.invoiceNumber ? `Invoice #${inv.invoiceNumber}` : `Invoice (${String(invoiceId).slice(0, 6)}…)`;
        return 'Invoice (unavailable)';
    };
    const describeEntry = (e) => {
        const amt = formatCurrency(Number(e.amount) || 0);
        switch (e.reason) {
            case 'origination': return { text: 'Credit created', amount: `+${amt}`, tone: 'pos' };
            case 'application': return { text: `Applied to ${invLabel(e.invoiceId)}`, amount: `−${amt}`, tone: 'neg' };
            case 'reversal': return { text: `Unapplied from ${invLabel(e.invoiceId)}`, amount: `+${amt}`, tone: 'pos' };
            case 'refund': return { text: 'Refunded', amount: `−${amt}`, tone: 'neg' };
            case 'writeOff': return { text: 'Written off', amount: `−${amt}`, tone: 'neg' };
            default: return { text: e.reason || 'Entry', amount: amt, tone: 'neutral' };
        }
    };
    const sortedCredits = [...credits].sort((a, b) => tsMillis(b.originatedAt) - tsMillis(a.originatedAt));
    const sortedEntries = (arr) => [...arr].sort((a, b) => tsMillis(a.effectiveAt || a.createdAt) - tsMillis(b.effectiveAt || b.createdAt));

    return (
        <div>
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                <div className="bg-white border rounded-lg p-4 shadow-sm">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Billed</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(totals.totalBilled)}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 shadow-sm">
                    <div className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Total Paid</div>
                    <div className="text-2xl font-bold text-emerald-800 mt-1">{formatCurrency(totals.totalPaid)}</div>
                </div>
                <div className={`border rounded-lg p-4 shadow-sm ${
                    totals.outstanding > 0 ? 'bg-red-50 border-red-100' : 'bg-white border-gray-200'
                }`}>
                    <div className={`text-xs font-medium uppercase tracking-wide ${
                        totals.outstanding > 0 ? 'text-red-700' : 'text-gray-500'
                    }`}>Outstanding</div>
                    <div className={`text-2xl font-bold mt-1 ${
                        totals.outstanding > 0 ? 'text-red-800' : 'text-gray-900'
                    }`}>{formatCurrency(totals.outstanding)}</div>
                </div>
                {/* Seam 7 — Account Credit (always shown; muted at $0, purple when positive). */}
                <div className={`border rounded-lg p-4 shadow-sm ${
                    accountCredit > 0 ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'
                }`}>
                    <div className={`text-xs font-medium uppercase tracking-wide ${
                        accountCredit > 0 ? 'text-purple-700' : 'text-gray-500'
                    }`}>Account Credit</div>
                    <div className={`text-2xl font-bold mt-1 ${
                        accountCredit > 0 ? 'text-purple-800' : 'text-gray-900'
                    }`}>{formatCurrency(accountCredit)}</div>
                </div>
            </div>

            {/* Disclaimer when imported orphan payments exist */}
            {totals.showDisclaimer && (
                <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                        This customer has <span className="font-semibold">{formatCurrency(totals.importedOrphanTotal)}</span> in imported payments not tied to specific invoices. The Outstanding figure may be lower in practice.
                    </p>
                </div>
            )}

            {/* Ledger table */}
            <div className="bg-white rounded-lg border shadow-sm overflow-x-auto w-full">
                <table className="w-full text-sm text-left whitespace-nowrap">
                    <thead className="bg-gray-50 text-gray-500 font-medium border-b">
                        <tr>
                            <SortableHeader label="Date" field="date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                            <SortableHeader label="Method" field="method" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                            <SortableHeader label="Reference" field="reference" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                            <SortableHeader label="Invoice #" field="invoice" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                            <SortableHeader label="Amount" field="amount" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
                            <th className="px-4 py-3 text-right text-gray-500">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ledger.length === 0 && (
                            <tr>
                                <td colSpan="6" className="text-center py-8 text-gray-400">No payments yet.</td>
                            </tr>
                        )}
                        {ledger.map(p => (
                            <tr key={p.key} className="border-b hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-700">{formatLedgerDate(p.date)}</td>
                                <td className="px-4 py-3 font-medium text-gray-900">
                                    {p.method}
                                    {p.isRefund && (
                                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Refund</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-gray-600">{p.reference || '—'}</td>
                                <td className="px-4 py-3">
                                    {p.invoiceNumber ? (
                                        <button
                                            onClick={() => onInvoiceClick(p.invoiceId)}
                                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                            title="View invoice"
                                        >
                                            #{p.invoiceNumber}
                                        </button>
                                    ) : (
                                        <span className="text-gray-400">—</span>
                                    )}
                                </td>
                                <td className={`px-4 py-3 text-right font-semibold tabular-nums ${
                                    p.amount < 0 ? 'text-orange-700' : 'text-emerald-700'
                                }`}>
                                    {formatCurrency(p.amount)}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    {p.syncStatus === 'imported' && (
                                        <span className="text-gray-500 inline-flex items-center justify-end gap-1">
                                            <CheckCircle size={14} /> Imported
                                        </span>
                                    )}
                                    {p.syncStatus === 'synced' && (
                                        <span className="text-green-600 inline-flex items-center justify-end gap-1">
                                            <CheckCircle size={14} /> Synced
                                        </span>
                                    )}
                                    {p.syncStatus === 'failed' && (
                                        <span className="text-red-600 inline-flex items-center justify-end gap-1">
                                            <AlertTriangle size={14} /> QB Failed
                                        </span>
                                    )}
                                    {p.syncStatus === 'local' && (
                                        <span className="text-gray-400">Local Only</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Seam 7 Part B — Account Credits: where each credit came from, what
                remains, and (expanded) its full ledger. Invoice numbers resolve
                from the in-memory invoice map; entries fetch once per open. */}
            <div className="mt-8">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Account Credits</h3>
                {sortedCredits.length === 0 ? (
                    <div className="bg-white rounded-lg border shadow-sm p-6 text-center text-gray-400 text-sm">No account credits.</div>
                ) : (
                    <div className="bg-white rounded-lg border shadow-sm divide-y">
                        {sortedCredits.map(c => {
                            const expanded = expandedCreditId === c.id;
                            const entries = ledgerEntriesByCredit[c.id];
                            const loading = loadingCreditId === c.id;
                            return (
                                <div key={c.id}>
                                    <button
                                        type="button"
                                        onClick={() => onToggleCredit && onToggleCredit(c.id)}
                                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-medium text-gray-900 truncate">{creditSourceLabel(c)}</div>
                                            <div className="text-xs text-gray-500 truncate">
                                                {fmtCreditDate(c.originatedAt)}{c.memo ? ` · ${c.memo}` : ''}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 shrink-0">
                                            <div className="text-right">
                                                <div className="text-[10px] text-gray-400 uppercase tracking-wide">Remaining</div>
                                                <div className="font-semibold tabular-nums text-gray-900">{formatCurrency(Number(c.availableBalance) || 0)}</div>
                                            </div>
                                            <div className="text-right hidden sm:block">
                                                <div className="text-[10px] text-gray-400 uppercase tracking-wide">Original</div>
                                                <div className="tabular-nums text-gray-600">{formatCurrency(Number(c.originalAmount) || 0)}</div>
                                            </div>
                                            <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${CREDIT_STATUS_STYLES[c.status] || 'bg-gray-100 text-gray-600'}`}>
                                                {c.status || 'unknown'}
                                            </span>
                                            {expanded
                                                ? <ChevronUp size={16} className="text-gray-400" />
                                                : <ChevronDown size={16} className="text-gray-400" />}
                                        </div>
                                    </button>
                                    {expanded && (
                                        <div className="px-4 pb-4 bg-gray-50/60">
                                            {/* Seam 15 -- spread this credit's remaining balance across open
                                                invoices oldest-first. Placed ABOVE the ledger so it is visible
                                                without scrolling past a long history. Shown only to
                                                isOwnerOrManager (client mirror of CREDIT_MANAGE_ROLES) on an
                                                open, positive-balance credit; a depleted/terminal credit or a
                                                lower-privilege viewer never sees it. */}
                                            {isOwnerOrManager && c.status === 'open' && (Number(c.availableBalance) || 0) > 0.01 && (
                                                <div className="pt-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => onSpreadCredit && onSpreadCredit(c)}
                                                        className="inline-flex items-center gap-2 bg-purple-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-purple-700 shadow-sm"
                                                    >
                                                        <Plus size={14} /> Apply to open invoices
                                                    </button>
                                                </div>
                                            )}
                                            {loading && !entries ? (
                                                <div className="py-4 text-sm text-gray-400 flex items-center gap-2">
                                                    <Loader2 size={14} className="animate-spin" /> Loading…
                                                </div>
                                            ) : (entries && entries.length > 0) ? (
                                                <ul className="divide-y">
                                                    {sortedEntries(entries).map(e => {
                                                        const d = describeEntry(e);
                                                        return (
                                                            <li key={e.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                                                                <span className="text-gray-700 min-w-0 truncate">
                                                                    {d.text}
                                                                    <span className="text-gray-400"> · {fmtCreditDate(e.effectiveAt || e.createdAt)}</span>
                                                                </span>
                                                                <span className={`tabular-nums font-medium shrink-0 ${
                                                                    d.tone === 'neg' ? 'text-orange-700' : d.tone === 'pos' ? 'text-emerald-700' : 'text-gray-600'
                                                                }`}>{d.amount}</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : (
                                                <div className="py-4 text-sm text-gray-400">No ledger entries.</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerFormModal;